// src/App.js
import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { ApiError, apiRequest, clearStoredAuth, getStoredAuth, setStoredAuth } from './apiClient';
import ScreenerSection from './components/ScreenerSection';
import DepotSection from './components/DepotSection';
import { getFiniteNumber, getPositionCostBasis, getPositionMarketValue, getPositionProfitLoss, getPositionProfitLossPercent, getPositionAnalysis } from './components/depotValuation';
import RslJournalSection from './components/RslJournalSection';
import AccountSection from './components/AccountSection';
import AdminPanel from './components/AdminPanel';
import IbAnalysisSection from './components/IbAnalysisSection';
import SupportSection from './components/SupportSection';
import SignalsSection from './components/SignalsSection';
import CoachingSection from './components/CoachingSection';
import AutomationSection from './components/AutomationSection';
import TradeJournalSection from './components/TradeJournalSection';
import RslBacktestSection from './components/RslBacktestSection';
import IndexInvestSection from './components/IndexInvestSection';
import AuthSection from './components/AuthSection';
import NewsletterPrompt from './components/NewsletterPrompt';
import ToastHost from './components/ToastHost';
import ConfirmDialog from './components/ConfirmDialog';
import ErrorBoundary from './components/ErrorBoundary';
import useBillingCheckoutNotice from './components/useBillingCheckoutNotice';
import { BUILD_ID } from './generated/buildInfo';
import { NEWSLETTER_CONSENT_VERSION } from './components/newsletterConsent';
import { ScreenerTabelle, DepotRows } from './components/depotTables';
import {
  buildUiHelpers,
  getPositionSignalInfo,
  sortBySpec,
  getDepotSectionIndexRsl,
} from './components/sharedUi';
import {
  formatDateTime,
  formatDate,
  formatMoney,
  formatPlanLabel,
  getPlanBadgeColor,
  getPositionCurrencyLabel,
} from './components/sharedFormatters';
import {
  MODULE_LIST,
  PLAN_MODULE_MATRIX_ROWS,
  SUPPORT_CATEGORIES,
  SUPPORT_STATUSES,
  SIGNAL_KINDS,
  RSL_WEEKDAYS,
  PLAN_LIST,
  PLAN_STATUS_LIST,
  PLAN_FEATURES,
  FEATURE_TOOLTIPS,
  getPathForView,
  getViewFromPathname,
} from './components/appConstants';
import { getThemeTokens } from './components/sharedTheme';

const TRADE_JOURNAL_REQUEST_TIMEOUT_MS = 30000;
const TRADE_JOURNAL_LIVE_PNL_TIMEOUT_MS = 600000;
const TRADE_JOURNAL_LIVE_PNL_STORAGE_KEY = 'trade_journal_live_pnl_snapshot_v6';
const PREFERRED_START_VIEW_STORAGE_KEY = 'preferredStartView';
const REGISTRATION_ATTRIBUTION_STORAGE_KEY = 'registrationAttribution';
const RISK_DISCLAIMER_VERSION = '1.0.8';
const ACQUISITION_REMINDER_DAYS = 7;

function AppFooter({ COLORS }) {
  return (
    <footer style={{
      padding: '18px 24px 26px',
      textAlign: 'center',
      color: COLORS.muted,
      fontSize: '13px',
      lineHeight: 1.7,
    }}>
      <a
        href="https://www.einfach-trading.de/rechtliches.html"
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: COLORS.muted, textDecoration: 'none' }}
      >
        Impressum &middot; OS-Plattform &middot; Datenschutz &middot; AGB
      </a>
      <br />
      <a
        href="https://www.einfach-trading.de"
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: COLORS.muted, textDecoration: 'none' }}
      >
        &copy; 2026 einfach-trading.de
      </a>
    </footer>
  );
}
const APP_VERSION_CHECK_INTERVAL_MS = 5 * 60 * 1000;
const ACQUISITION_SOURCE_OPTIONS = [
  { value: 'youtube', label: 'YouTube' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'telegram', label: 'Telegram' },
  { value: 'google', label: 'Google-Suche' },
  { value: 'recommendation', label: 'Empfehlung von Freunden oder Bekannten' },
  { value: 'event', label: 'Webinar oder Veranstaltung' },
  { value: 'podcast', label: 'Podcast' },
  { value: 'newsletter', label: 'Newsletter' },
  { value: 'partner', label: 'Kooperationspartner oder Influencer' },
  { value: 'other', label: 'Sonstiges' },
];

function captureRegistrationAttribution() {
  if (typeof window === 'undefined') return {};
  try {
    const previous = JSON.parse(window.sessionStorage.getItem(REGISTRATION_ATTRIBUTION_STORAGE_KEY) || '{}');
    const params = new URLSearchParams(window.location.search);
    const attribution = {
      registration_referrer: previous.registration_referrer || document.referrer || '',
      registration_utm_source: previous.registration_utm_source || params.get('utm_source') || '',
      registration_utm_medium: previous.registration_utm_medium || params.get('utm_medium') || '',
      registration_utm_campaign: previous.registration_utm_campaign || params.get('utm_campaign') || '',
    };
    window.sessionStorage.setItem(REGISTRATION_ATTRIBUTION_STORAGE_KEY, JSON.stringify(attribution));
    return attribution;
  } catch (_err) {
    return {};
  }
}
const VIEW_LABELS = {
  start: 'Start',
  depot: 'Depot',
  rsl_journal: 'Journal',
  screener: 'Scanner',
  rsl_backtest: 'Backtest',
  trade_journal: 'IB-Journal',
  kontoauszug: 'IB-Auswertung',
  coaching: 'Anleitung',
  academy: 'Automation',
  signals: 'Signale',
  index_invest: 'Index-Invest',
  support: 'Support',
  konto: 'Profil',
  admin: 'Admin',
};
const MANUAL_ADD_MARKET_LABELS = {
  SP500: 'S&P 500',
  HDAX: 'HDAX',
  NASDAQ100: 'Nasdaq 100',
  STOXX600: 'STOXX 600',
};
const RISK_DISCLAIMER_SECTIONS = [
  {
    title: '1. Allgemeine Informationen - keine Anlageberatung',
    text: 'Die auf dieser Website sowie über verbundene Dienste (z. B. Plattformen, Tools, Kommunikationskanäle) bereitgestellten Inhalte dienen ausschließlich allgemeinen Informations- und Bildungszwecken. Sie stellen weder eine Anlageberatung noch eine Anlagevermittlung, Finanzportfolioverwaltung oder sonstige erlaubnispflichtige Finanzdienstleistung dar. Insbesondere erfolgen keine individuellen Empfehlungen oder Beratungen unter Berücksichtigung der persönlichen Umstände, finanziellen Situation oder Anlageziele des Nutzers.',
  },
  {
    title: '2. Keine Aufforderung zum Handeln',
    text: 'Die bereitgestellten Inhalte - einschließlich etwaiger Analysen, Auswertungen, Markteinschätzungen oder algorithmisch generierter Hinweise („Signale“) - stellen keine Aufforderung zum Kauf, Verkauf oder Halten von Finanzinstrumenten dar. Alle Entscheidungen erfolgen ausschließlich eigenverantwortlich durch den Nutzer.',
  },
  {
    title: '3. Risiko von Finanzgeschäften',
    text: 'Der Handel mit Finanzinstrumenten, insbesondere mit Derivaten oder gehebelten Produkten, ist mit erheblichen Risiken verbunden und kann zum vollständigen Verlust des eingesetzten Kapitals führen. Frühere Wertentwicklungen, Simulationen oder Prognosen sind kein verlässlicher Indikator für zukünftige Ergebnisse. Der Nutzer ist verpflichtet, sich eigenständig über Risiken zu informieren und zu prüfen, ob entsprechende Geschäfte seiner finanziellen Situation und Risikobereitschaft entsprechen.',
  },
  {
    title: '4. Keine Gewähr für Inhalte und Daten',
    text: 'Es wird keine Gewähr für die Richtigkeit, Vollständigkeit oder Aktualität der bereitgestellten Inhalte, Daten oder Informationen übernommen. Inhalte können unvollständig, fehlerhaft oder zeitlich verzögert sein.',
  },
  {
    title: '5. Marktdaten und Datenquellen',
    text: 'Soweit Marktdaten verwendet werden, können diese von Drittanbietern stammen. Es wird keine Garantie für deren Richtigkeit, Vollständigkeit oder Lizenzkonformität übernommen. Eine Weitergabe, Vervielfältigung oder kommerzielle Nutzung durch den Nutzer kann lizenzrechtlichen Beschränkungen unterliegen.',
  },
  {
    title: '6. Haftungsbeschränkung',
    text: 'Die Haftung für Schäden ist ausgeschlossen, soweit diese nicht auf vorsätzlichem oder grob fahrlässigem Verhalten beruhen. Bei leicht fahrlässiger Verletzung wesentlicher Vertragspflichten (Kardinalpflichten) ist die Haftung auf den vertragstypischen, vorhersehbaren Schaden begrenzt. Eine weitergehende Haftung ist ausgeschlossen. Die Haftung für Schäden aus der Verletzung von Leben, Körper oder Gesundheit bleibt unberührt.',
  },
  {
    title: '7. Keine steuerliche oder rechtliche Beratung',
    text: 'Die bereitgestellten Inhalte stellen keine steuerliche oder rechtliche Beratung dar. Im Zweifel ist ein entsprechend qualifizierter Berater zu konsultieren.',
  },
  {
    title: '8. Eigenverantwortung des Nutzers',
    text: 'Der Nutzer handelt ausschließlich auf eigene Verantwortung. Eine Überwachung, Kontrolle oder Steuerung von Handelsentscheidungen durch den Anbieter findet nicht statt.',
  },
  {
    title: '9. Zustimmung',
    text: 'Mit der Nutzung der bereitgestellten Inhalte bestätigt der Nutzer, den Risikohinweis und Haftungsausschluss zur Kenntnis genommen zu haben, und erkennt die vorstehenden Bestimmungen als verbindlich an. Soweit technisch möglich, bestätigt der Nutzer durch aktive Zustimmung (z. B. Checkbox vor Nutzung), dass er diesen Risikohinweis und Haftungsausschluss gelesen, verstanden und akzeptiert hat.',
  },
];

function todayInputValue() {
  const now = new Date();
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
}

// ─── HAUPT APP ────────────────────────────────────────────

export default function App() {
  const readStoredTradeJournalLivePnl = () => {
    try {
      const raw = window.sessionStorage.getItem(TRADE_JOURNAL_LIVE_PNL_STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch {
      return null;
    }
  };
  const [token, setToken]             = useState(() => {
    const stored = getStoredAuth();
    return stored.sessionActive ? '__cookie__' : '';
  });
  const [themeMode, setThemeMode]     = useState(() => localStorage.getItem('themeMode') || 'dark');
  const initialVerificationLinkToken = (() => {
    if (window.location.pathname !== '/email-bestaetigen') return '';
    return new URLSearchParams(window.location.search).get('token') || '';
  })();
  const initialLoginLinkToken = (() => {
    if (window.location.pathname !== '/login-bestaetigen') return '';
    return new URLSearchParams(window.location.search).get('token') || '';
  })();
  const [authMode, setAuthMode]       = useState(
    initialVerificationLinkToken ? 'verifylink' : (initialLoginLinkToken ? 'loginlink' : 'login')
  );
  const [email, setEmail]             = useState('');
  const [password, setPassword]       = useState('');
  const [loginFehler, setLoginFehler] = useState('');
  const [loginFailureCount, setLoginFailureCount] = useState(0);
  const [loginWaitSeconds, setLoginWaitSeconds] = useState(0);
  const [loginHelpMsg, setLoginHelpMsg] = useState('');
  const [loginHelpErr, setLoginHelpErr] = useState('');
  const [loginHelpLoading, setLoginHelpLoading] = useState('');
  const [loginLinkToken, setLoginLinkToken] = useState(initialLoginLinkToken);
  const [loginLinkMsg, setLoginLinkMsg] = useState('');
  const [loginLinkErr, setLoginLinkErr] = useState('');
  const [loginLinkSaving, setLoginLinkSaving] = useState(false);
  const [registerEmail, setRegisterEmail] = useState('');
  const [registerPassword, setRegisterPassword] = useState('');
  const [registerNewsletter, setRegisterNewsletter] = useState(true);
  const [newsletterStatus, setNewsletterStatus] = useState('loading');
  const [keepNewsletter, setKeepNewsletter] = useState(false);
  const [registerMsg, setRegisterMsg] = useState('');
  const [registerErr, setRegisterErr] = useState('');
  const [verifyEmail, setVerifyEmail] = useState('');
  const [verifyCode, setVerifyCode]   = useState('');
  const [verifyMsg, setVerifyMsg]     = useState('');
  const [verifyErr, setVerifyErr]     = useState('');
  const [verificationLinkToken, setVerificationLinkToken] = useState(initialVerificationLinkToken);
  const [verificationLinkMsg, setVerificationLinkMsg] = useState('');
  const [verificationLinkErr, setVerificationLinkErr] = useState('');
  const [verificationLinkSaving, setVerificationLinkSaving] = useState(false);
  const [login2faCode, setLogin2faCode] = useState('');
  const [login2faMsg, setLogin2faMsg] = useState('');
  const [login2faErr, setLogin2faErr] = useState('');
  const [preauthToken, setPreauthToken] = useState('');
  const [resetEmail, setResetEmail]   = useState('');
  const [resetCode, setResetCode]     = useState('');
  const [resetPassword, setResetPassword] = useState('');
  const [resetMsg, setResetMsg]       = useState('');
  const [resetErr, setResetErr]       = useState('');

  useEffect(() => {
    if (loginWaitSeconds <= 0) return undefined;
    const timer = window.setInterval(() => {
      setLoginWaitSeconds(current => Math.max(0, current - 1));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [loginWaitSeconds]);
  const [isAdmin, setIsAdmin]         = useState(false);
  const [accountPlan, setAccountPlan] = useState('free');
  const [accountPlanStatus, setAccountPlanStatus] = useState('active');
  const [subscriptionUntil, setSubscriptionUntil] = useState('');
  const [billingStatus, setBillingStatus] = useState('none');
  const [billingCurrentPeriodEnd, setBillingCurrentPeriodEnd] = useState('');
  const [billingCancelAtPeriodEnd, setBillingCancelAtPeriodEnd] = useState(false);
  const [billingAccessUntil, setBillingAccessUntil] = useState('');
  const [billingDeletionScheduledFor, setBillingDeletionScheduledFor] = useState('');
  const [billingCustomerId, setBillingCustomerId] = useState('');
  const [billingHistory, setBillingHistory] = useState([]);
  const [billingNotice, setBillingNotice] = useState('');
  const [billingConfig, setBillingConfig] = useState({
    billing_enabled: false,
    provider: 'stripe',
    portal_enabled: false,
    basic_monthly_available: false,
    basic_yearly_available: false,
    premium_monthly_available: false,
    premium_yearly_available: false,
    coaching_available: false,
  });
  const [billingActionLoading, setBillingActionLoading] = useState('');
  const [modulesLoaded, setModulesLoaded] = useState(false);
  const [moduleAccess, setModuleAccess] = useState({
    start: false,
    konto: false,
    screener: false,
    rsl_backtest: false,
    depot: false,
    trade_journal: false,
    kontoauszug: false,
    coaching: false,
    academy: false,
    support: false,
    signals: false,
    index_invest: false,
    custom_rsl_watchlists: false,
  });
  const [uiSettings, setUiSettings] = useState({
    tradingview_charts_enabled: false,
  });
  const [view, setView]               = useState(() => getViewFromPathname(window.location.pathname));
  const [preferredStartView, setPreferredStartView] = useState(() => localStorage.getItem(PREFERRED_START_VIEW_STORAGE_KEY) || 'start');
  const [preferredStartViewMsg, setPreferredStartViewMsg] = useState('');
  const [preferredStartViewErr, setPreferredStartViewErr] = useState('');
  const [changelogEntries, setChangelogEntries] = useState([]);
  const [kontoauszugErr, setKontoauszugErr] = useState('');
  const [kontoauszugMsg, setKontoauszugMsg] = useState('');
  const [kontoauszugLoading, setKontoauszugLoading] = useState(false);
  const [kontoauszugFile, setKontoauszugFile] = useState(null);
  const [kontoauszugYear, setKontoauszugYear] = useState(new Date().getFullYear());
  const [ibTaxYears, setIbTaxYears] = useState([]);
  const [kontoauszugMeta, setKontoauszugMeta] = useState(null);
  const [kontoauszugReferences, setKontoauszugReferences] = useState([]);
  const [kontoauszugResults, setKontoauszugResults] = useState([]);
  const [kontoauszugIssues, setKontoauszugIssues] = useState([]);
  const [kontoauszugShowReferences, setKontoauszugShowReferences] = useState(false);
  const [kontoauszugJobId, setKontoauszugJobId] = useState('');
  const [kontoauszugProgress, setKontoauszugProgress] = useState(0);
  const [kontoauszugProgressText, setKontoauszugProgressText] = useState('');
  const [supportTickets, setSupportTickets] = useState([]);
  const [supportTicketTotal, setSupportTicketTotal] = useState(0);
  const [supportTicketOffset, setSupportTicketOffset] = useState(0);
  const [supportTicketLimit] = useState(5);
  const [supportDetail, setSupportDetail] = useState(null);
  const [supportLoading, setSupportLoading] = useState(false);
  const [supportErr, setSupportErr] = useState('');
  const [supportMsg, setSupportMsg] = useState('');
  const [supportStatusFilter, setSupportStatusFilter] = useState('open');
  const [supportDeleteConfirmText, setSupportDeleteConfirmText] = useState('');
  const [supportSubject, setSupportSubject] = useState('');
  const [supportCategory, setSupportCategory] = useState('Allgemeine Frage');
  const [supportMessage, setSupportMessage] = useState('');
  const [supportReply, setSupportReply] = useState('');
  const [supportStatusValue, setSupportStatusValue] = useState('offen');
  const [signalsEntries, setSignalsEntries] = useState([]);
  const [marketSentiment, setMarketSentiment] = useState(null);
  const [marketSignalCards, setMarketSignalCards] = useState([]);
  const [signalsLoading, setSignalsLoading] = useState(false);
  const [signalsErr, setSignalsErr] = useState('');
  const [signalsMsg, setSignalsMsg] = useState('');
  const [marketSignalsErr, setMarketSignalsErr] = useState('');
  const [marketSignalsMsg, setMarketSignalsMsg] = useState('');
  const [signalKind, setSignalKind] = useState('open');
  const [signalText, setSignalText] = useState('');
  const [signalRelatedId, setSignalRelatedId] = useState('');
  const [signalEditId, setSignalEditId] = useState(null);
  const [signalFilter, setSignalFilter] = useState('');
  const [signalViewMode, setSignalViewMode] = useState('all');
  const [signalOpenLimit, setSignalOpenLimit] = useState(8);
  const [signalCloseLimit, setSignalCloseLimit] = useState(8);
  const [screenerTab, setScreenerTab] = useState('SP500');

  const { COLORS, th, td, inputStyle } = useMemo(() => getThemeTokens(themeMode), [themeMode]);
  const { Card, Badge, Btn } = useMemo(() => buildUiHelpers({ COLORS, th }), [COLORS, th]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    localStorage.setItem('themeMode', themeMode);
  }, [themeMode]);

  useEffect(() => {
    let cancelled = false;

    async function pruefeBuildVersion() {
      try {
        const response = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' });
        if (!response.ok) return;
        const data = await response.json();
        const latestBuildId = String(data?.buildId || '').trim();
        if (!latestBuildId || cancelled) return;
        if (!currentBuildIdRef.current) {
          currentBuildIdRef.current = latestBuildId;
          return;
        }
        if (latestBuildId !== currentBuildIdRef.current) {
          setAppUpdateAvailable(true);
        }
      } catch {
        // Version checks must never interrupt normal app usage.
      }
    }

    pruefeBuildVersion();
    const intervalId = window.setInterval(pruefeBuildVersion, APP_VERSION_CHECK_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, []);

  useEffect(() => {
    localStorage.setItem(PREFERRED_START_VIEW_STORAGE_KEY, preferredStartView);
  }, [preferredStartView]);

  const toggleThemeMode = () => {
    setThemeMode((current) => (current === 'dark' ? 'light' : 'dark'));
  };

  useBillingCheckoutNotice(setBillingNotice);

  const [ergebnis, setErgebnis]   = useState(null);
  const [laden, setLaden]         = useState(false);
  const [statusMsg, setStatusMsg] = useState('');
  const [cachedAt, setCachedAt]   = useState(null);
  const [cachedAtByMarket, setCachedAtByMarket] = useState({});
  const [marketDataAsOf, setMarketDataAsOf] = useState({ SP500: null, HDAX: null, NASDAQ100: null, STOXX600: null });
  const [customRslWatchlists, setCustomRslWatchlists] = useState([]);
  const [customRslWatchlistsLoading, setCustomRslWatchlistsLoading] = useState(false);
  const [customRslWatchlistsError, setCustomRslWatchlistsError] = useState('');

  const [depot, setDepot]           = useState([]);
  const [depotLaden, setDepotLaden] = useState(false);
  const [manuellerTicker, setManuellerTicker] = useState('');
  const [tickerFehler, setTickerFehler]       = useState('');
  const [tickerErfolg, setTickerErfolg]       = useState('');
  const [manualAddIndexChoice, setManualAddIndexChoice] = useState(null);
  const [pwCurrent, setPwCurrent]             = useState('');
  const [pwNew, setPwNew]                     = useState('');
  const [pwConfirm, setPwConfirm]             = useState('');
  const [pwMsg, setPwMsg]                     = useState('');
  const [pwErr, setPwErr]                     = useState('');
  const [pwSaving, setPwSaving]               = useState(false);
  const [emailNotificationsEnabled, setEmailNotificationsEnabled] = useState(true);
  const [premiumSignalEmailsEnabled, setPremiumSignalEmailsEnabled] = useState(true);
  const [rslEmailDays, setRslEmailDays] = useState([]);
  const [emailTheme, setEmailTheme] = useState('dark');
  const [tradeJournalAccountLabel, setTradeJournalAccountLabel] = useState('');
  const [tradeJournalQueryId, setTradeJournalQueryId] = useState('');
  const [tradeJournalFlexToken, setTradeJournalFlexToken] = useState('');
  const [tradeJournalFlexTokenSet, setTradeJournalFlexTokenSet] = useState(false);
  const [tradeJournalAgentSecretSet, setTradeJournalAgentSecretSet] = useState(false);
  const [tradeJournalAgentSecretValue, setTradeJournalAgentSecretValue] = useState('');
  const [tradeJournalAgentGenerating, setTradeJournalAgentGenerating] = useState(false);
  const [tradeJournalAgentName, setTradeJournalAgentName] = useState('');
  const [tradeJournalAgentVersion, setTradeJournalAgentVersion] = useState('');
  const [, setTradeJournalAgentStatus] = useState('not_configured');
  const [tradeJournalAgentStatusLabel, setTradeJournalAgentStatusLabel] = useState('Nicht eingerichtet');
  const [, setTradeJournalAgentStatusDetail] = useState('');
  const [tradeJournalAgentStatusColor, setTradeJournalAgentStatusColor] = useState('ghost');
  const [tradeJournalAgentLastSeenAt, setTradeJournalAgentLastSeenAt] = useState('');
  const [, setTradeJournalAgentLastRequestAt] = useState('');
  const [tradeJournalAgentLastQuoteAt, setTradeJournalAgentLastQuoteAt] = useState('');
  const [tradeJournalAgentLastError, setTradeJournalAgentLastError] = useState('');
  const [tradeJournalAgentLastErrorAt, setTradeJournalAgentLastErrorAt] = useState('');
  const [tradeJournalAgentTwsConnected, setTradeJournalAgentTwsConnected] = useState(null);
  const [tradeJournalAgentLastTwsConnectedAt, setTradeJournalAgentLastTwsConnectedAt] = useState('');
  const [tradeJournalAgentLiveTwsConnected, setTradeJournalAgentLiveTwsConnected] = useState(null);
  const [tradeJournalAgentLiveLastTwsConnectedAt, setTradeJournalAgentLiveLastTwsConnectedAt] = useState('');
  const [tradeJournalAgentPaperTwsConnected, setTradeJournalAgentPaperTwsConnected] = useState(null);
  const [tradeJournalAgentPaperLastTwsConnectedAt, setTradeJournalAgentPaperLastTwsConnectedAt] = useState('');
  const [tradeJournalAgentLastQuoteDurationMs, setTradeJournalAgentLastQuoteDurationMs] = useState(null);
  const [tradeJournalLastSyncAt, setTradeJournalLastSyncAt] = useState('');
  const [tradeJournalLastSyncStatus, setTradeJournalLastSyncStatus] = useState('');
  const [tradeJournalLastSyncError, setTradeJournalLastSyncError] = useState('');
  const [tradeJournalLastSyncImportedCount, setTradeJournalLastSyncImportedCount] = useState(0);
  const [tradeJournalLastSyncSkippedCount, setTradeJournalLastSyncSkippedCount] = useState(0);
  const [tradeJournalLastSyncItems, setTradeJournalLastSyncItems] = useState([]);
  const [tradeJournalMsg, setTradeJournalMsg] = useState('');
  const [tradeJournalErr, setTradeJournalErr] = useState('');
  const [tradeJournalSaving, setTradeJournalSaving] = useState(false);
  const [tradeJournalLoading, setTradeJournalLoading] = useState(false);
  const [tradeJournalGroups, setTradeJournalGroups] = useState([]);
  const [tradeJournalGroupPage, setTradeJournalGroupPage] = useState({ items: [], total: 0, limit: 50, offset: 0, cache_hit: false, cached_at: null });
  const [tradeJournalGroupPageLoading, setTradeJournalGroupPageLoading] = useState(false);
  const [tradeJournalOverview, setTradeJournalOverview] = useState(null);
  const [tradeJournalOverviewLoading, setTradeJournalOverviewLoading] = useState(false);
  const [tradeJournalCashSummary, setTradeJournalCashSummary] = useState(null);
  const [tradeJournalSyncJobId, setTradeJournalSyncJobId] = useState('');
  const [tradeJournalSyncStatus, setTradeJournalSyncStatus] = useState('');
  const [tradeJournalSyncProgress, setTradeJournalSyncProgress] = useState(0);
  const [tradeJournalSyncMessage, setTradeJournalSyncMessage] = useState('');
  const [tradeJournalSyncFetchedCount, setTradeJournalSyncFetchedCount] = useState(0);
  const [tradeJournalSyncImportedCount, setTradeJournalSyncImportedCount] = useState(0);
  const [tradeJournalSyncSkippedCount, setTradeJournalSyncSkippedCount] = useState(0);
  const [tradeJournalSyncImportedItems, setTradeJournalSyncImportedItems] = useState([]);
  const [tradeJournalSyncError, setTradeJournalSyncError] = useState('');
  const [tradeJournalSyncLoading, setTradeJournalSyncLoading] = useState(false);
  const [tradeJournalCashTransactions, setTradeJournalCashTransactions] = useState([]);
  const [tradeJournalCashTransactionsLoaded, setTradeJournalCashTransactionsLoaded] = useState(false);
  const [tradeJournalSourceFilter, setTradeJournalSourceFilter] = useState('all');
  const [tradeJournalStatementSummary, setTradeJournalStatementSummary] = useState(null);
  const [tradeJournalForexPnl] = useState([]);
  const [tradeJournalSetups, setTradeJournalSetups] = useState([]);
  const [tradeJournalLivePnl, setTradeJournalLivePnl] = useState(() => readStoredTradeJournalLivePnl());
  const [tradeJournalLivePnlLoading, setTradeJournalLivePnlLoading] = useState(false);
  const [tradeJournalLivePnlError, setTradeJournalLivePnlError] = useState('');
  const [notificationMsg, setNotificationMsg] = useState('');
  const [notificationErr, setNotificationErr] = useState('');
  const [notificationSaving, setNotificationSaving] = useState(false);
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [twoFactorPending, setTwoFactorPending] = useState(false);
  const [twoFactorCurrentPassword, setTwoFactorCurrentPassword] = useState('');
  const [twoFactorVerificationCode, setTwoFactorVerificationCode] = useState('');
  const [twoFactorMsg, setTwoFactorMsg] = useState('');
  const [twoFactorErr, setTwoFactorErr] = useState('');
  const [twoFactorSaving, setTwoFactorSaving] = useState(false);
  const [riskAcceptedAt, setRiskAcceptedAt] = useState('');
  const [riskAcceptedEmail, setRiskAcceptedEmail] = useState('');
  const [riskAcceptedVersion, setRiskAcceptedVersion] = useState('');
  const [riskCurrentVersion, setRiskCurrentVersion] = useState(RISK_DISCLAIMER_VERSION);
  const [tradeJournalOrderFormEnabled, setTradeJournalOrderFormEnabled] = useState(false);
  const [riskAcceptSaving, setRiskAcceptSaving] = useState(false);
  const [riskAcceptErr, setRiskAcceptErr] = useState('');
  const [acquisitionSource, setAcquisitionSource] = useState('');
  const [acquisitionDetail, setAcquisitionDetail] = useState('');
  const [acquisitionAnsweredAt, setAcquisitionAnsweredAt] = useState('');
  const [acquisitionSkippedAt, setAcquisitionSkippedAt] = useState('');
  const [acquisitionSaving, setAcquisitionSaving] = useState(false);
  const [acquisitionErr, setAcquisitionErr] = useState('');
  const [appUpdateAvailable, setAppUpdateAvailable] = useState(false);
  const currentBuildIdRef = useRef(BUILD_ID || '');
  const [deletePw, setDeletePw]               = useState('');
  const [deleteMsg, setDeleteMsg]             = useState('');
  const [deleteErr, setDeleteErr]             = useState('');
  const [deleteSaving, setDeleteSaving]       = useState(false);

  // NEU: Historie
  const [historie, setHistorie]         = useState([]);
  const [closePosition, setClosePosition] = useState(null);
  const [closeDate, setCloseDate] = useState(todayInputValue);
  const [closePrice, setClosePrice] = useState('');
  const [closeSellQuantity, setCloseSellQuantity] = useState('');
  const [closeSaving, setCloseSaving] = useState(false);
  const [closeErr, setCloseErr] = useState('');
  const [historyEditId, setHistoryEditId] = useState(null);
  const [historyEditDraft, setHistoryEditDraft] = useState({});
  const [historyEditErr, setHistoryEditErr] = useState('');
  const [historyEditSaving, setHistoryEditSaving] = useState(false);
  const [depotSort, setDepotSort] = useState({ key: 'rang', direction: 'asc' });
  const [toast, setToast] = useState(null);
  const [confirmState, setConfirmState] = useState({ open: false, message: '' });
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const toastTimerRef = useRef(null);
  const confirmResolverRef = useRef(null);
  const moreMenuRef = useRef(null);
  const previousViewRef = useRef(null);
  const kontoauszugPollTimeoutRef = useRef(null);
  const tickerErfolgTimerRef = useRef(null);
  const tradeJournalSyncPollTimeoutRef = useRef(null);
  const tradeJournalDashboardRequestRef = useRef(0);
  const landingRedirectAppliedRef = useRef(false);
  const preferredStartRedirectPendingRef = useRef(false);

  const resetClientSession = useCallback(() => {
    setKeepNewsletter(false);
    setNewsletterStatus('loading');
    setRegisterNewsletter(true);
    tradeJournalDashboardRequestRef.current += 1;
    landingRedirectAppliedRef.current = false;
    preferredStartRedirectPendingRef.current = false;
    clearStoredAuth();
    setToken('');
    setAuthMode('login');
    setPassword('');
    setLoginFehler('');
    setRegisterPassword('');
    setRegisterMsg('');
    setRegisterErr('');
    setAcquisitionSource('');
    setAcquisitionDetail('');
    setAcquisitionAnsweredAt('');
    setAcquisitionSkippedAt('');
    setAcquisitionSaving(false);
    setAcquisitionErr('');
    setVerifyCode('');
    setVerifyMsg('');
    setVerifyErr('');
    setDepot([]);
    setErgebnis(null);
    setModulesLoaded(false);
    setAccountPlan('free');
    setAccountPlanStatus('active');
    setSubscriptionUntil('');
    setBillingStatus('none');
    setBillingCurrentPeriodEnd('');
    setBillingCancelAtPeriodEnd(false);
    setBillingAccessUntil('');
    setBillingDeletionScheduledFor('');
    setBillingCustomerId('');
    setBillingHistory([]);
    setBillingNotice('');
    setBillingConfig({
      billing_enabled: false,
      provider: 'stripe',
      portal_enabled: false,
      basic_monthly_available: false,
      basic_yearly_available: false,
      premium_monthly_available: false,
      premium_yearly_available: false,
      coaching_available: false,
    });
    setBillingActionLoading('');
    setModuleAccess({ start: false, konto: false, screener: false, rsl_backtest: false, depot: false, trade_journal: false, kontoauszug: false, coaching: false, academy: false, support: false, signals: false, index_invest: false, custom_rsl_watchlists: false });
    setTradeJournalAccountLabel('');
    setTradeJournalQueryId('');
    setTradeJournalFlexToken('');
    setTradeJournalFlexTokenSet(false);
    setTradeJournalAgentSecretSet(false);
    setTradeJournalAgentSecretValue('');
    setTradeJournalAgentGenerating(false);
    setTradeJournalAgentLastSeenAt('');
    setTradeJournalAgentLastRequestAt('');
    setTradeJournalAgentLastQuoteAt('');
    setTradeJournalAgentLastError('');
    setTradeJournalAgentTwsConnected(null);
    setTradeJournalAgentLastTwsConnectedAt('');
    setTradeJournalAgentLastQuoteDurationMs(null);
    setTradeJournalLastSyncAt('');
    setTradeJournalLastSyncStatus('');
    setTradeJournalLastSyncError('');
    setTradeJournalLastSyncImportedCount(0);
    setTradeJournalLastSyncSkippedCount(0);
    setTradeJournalLastSyncItems([]);
    setTradeJournalMsg('');
    setTradeJournalErr('');
    setTradeJournalSaving(false);
    setTradeJournalGroups([]);
    setTradeJournalGroupPage({ items: [], total: 0, limit: 50, offset: 0, cache_hit: false, cached_at: null });
    setTradeJournalGroupPageLoading(false);
    setTradeJournalOverview(null);
    setTradeJournalOverviewLoading(false);
    setTradeJournalCashSummary(null);
    setTradeJournalLoading(false);
    setTradeJournalSyncJobId('');
    setTradeJournalSyncStatus('');
    setTradeJournalSyncProgress(0);
    setTradeJournalSyncMessage('');
    setTradeJournalSyncFetchedCount(0);
    setTradeJournalSyncImportedCount(0);
    setTradeJournalSyncSkippedCount(0);
    setTradeJournalSyncImportedItems([]);
    setTradeJournalSyncError('');
    setTradeJournalSyncLoading(false);
    setTradeJournalCashTransactions([]);
    setTradeJournalCashTransactionsLoaded(false);
    setTradeJournalSetups([]);
    setKontoauszugErr('');
    setSupportTickets([]);
    setSupportTicketTotal(0);
    setSupportTicketOffset(0);
    setSupportDetail(null);
    setSupportLoading(false);
    setSupportErr('');
    setSupportMsg('');
    setSupportStatusFilter('open');
    setSupportDeleteConfirmText('');
    setSupportSubject('');
    setSupportCategory('Allgemeine Frage');
    setSupportMessage('');
    setSupportReply('');
    setSupportStatusValue('offen');
    setSignalsEntries([]);
    setSignalsLoading(false);
    setSignalsErr('');
    setSignalsMsg('');
    setSignalKind('open');
    setSignalText('');
    setSignalRelatedId('');
    setSignalEditId(null);
    setSignalFilter('');
    setSignalViewMode('all');
    setLogin2faCode('');
    setLogin2faMsg('');
    setLogin2faErr('');
    setPreauthToken('');
    setPwCurrent('');
    setPwNew('');
    setPwConfirm('');
    setPwMsg('');
    setPwErr('');
    setPwSaving(false);
    setEmailNotificationsEnabled(true);
    setPremiumSignalEmailsEnabled(true);
    setRslEmailDays([]);
    setNotificationMsg('');
    setNotificationErr('');
    setNotificationSaving(false);
    setTwoFactorEnabled(false);
    setTwoFactorPending(false);
    setTwoFactorCurrentPassword('');
    setTwoFactorVerificationCode('');
    setTwoFactorMsg('');
    setTwoFactorErr('');
    setTwoFactorSaving(false);
    setRiskAcceptedAt('');
    setRiskAcceptedEmail('');
    setRiskAcceptedVersion('');
    setRiskCurrentVersion(RISK_DISCLAIMER_VERSION);
    setRiskAcceptSaving(false);
    setRiskAcceptErr('');
    setIsAdmin(false);
    setUiSettings({ tradingview_charts_enabled: false });
    navigateToView('start', { replace: true });
  }, []);

  useEffect(() => {
    captureRegistrationAttribution();
  }, []);

  const handleSessionError = useCallback((err) => {
    if (err instanceof ApiError && err.status === 401) {
      resetClientSession();
      return true;
    }
    return false;
  }, [resetClientSession]);

  function dismissToast() {
    if (toastTimerRef.current) {
      window.clearTimeout(toastTimerRef.current);
      toastTimerRef.current = null;
    }
    setToast(null);
  }

  function showToast(message, type = 'info', options = {}) {
    const durationMs = Number.isFinite(Number(options.durationMs)) ? Number(options.durationMs) : 3200;
    dismissToast();
    setToast({
      message,
      type,
      actionLabel: options.actionLabel || '',
      onAction: typeof options.onAction === 'function' ? options.onAction : null,
    });
    toastTimerRef.current = window.setTimeout(() => {
      setToast(null);
      toastTimerRef.current = null;
    }, durationMs);
  }

  function openAccountPlanOverview() {
    dismissToast();
    navigateToView('konto');
    window.setTimeout(() => {
      const target = document.getElementById('account-plan-overview');
      if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 120);
  }

  function requestConfirm(message) {
    return new Promise(resolve => {
      confirmResolverRef.current = resolve;
      setConfirmState({ open: true, message });
    });
  }

  function closeConfirm(answer) {
    const resolver = confirmResolverRef.current;
    confirmResolverRef.current = null;
    setConfirmState({ open: false, message: '' });
    if (resolver) resolver(answer);
  }

  const runApi = useCallback(async (task, options = {}) => {
    const { onError, fallbackMessage = 'Aktion fehlgeschlagen', rethrow = false, defaultValue = null } = options;
    try {
      return await task();
    } catch (err) {
      if (handleSessionError(err)) {
        if (rethrow) throw err;
        return defaultValue;
      }
      if (onError) {
        onError(err?.message || fallbackMessage);
      }
      if (rethrow) throw err;
      return defaultValue;
    }
  }, [handleSessionError]);

  function navigateToView(nextView, options = {}) {
    const replace = !!options.replace;
    const nextPath = getPathForView(nextView);
    setMoreMenuOpen(false);
    setView(nextView);
    if (window.location.pathname !== nextPath) {
      const fn = replace ? window.history.replaceState : window.history.pushState;
      fn.call(window.history, {}, '', nextPath);
    }
  }

  useEffect(() => {
    const onPopState = () => {
      setView(getViewFromPathname(window.location.pathname));
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  useEffect(() => {
    if (!moreMenuOpen) return;
    const onMouseDown = event => {
      if (moreMenuRef.current && !moreMenuRef.current.contains(event.target)) {
        setMoreMenuOpen(false);
      }
    };
    const onKeyDown = event => {
      if (event.key === 'Escape') {
        setMoreMenuOpen(false);
      }
    };
    window.addEventListener('mousedown', onMouseDown);
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [moreMenuOpen]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!token) return;

    let mounted = true;

    (async () => {
      const [me, modules] = await Promise.all([ladeCurrentUser(), ladeModuleRechte()]);
      if (!mounted) return;

      setEmail(me?.email || '');
      setIsAdmin(!!me?.is_admin);
      setTradeJournalOrderFormEnabled(!!me?.is_admin || !!me?.trade_journal_order_form_enabled);
      setAccountPlan(me?.effective_plan || me?.plan || 'free');
      setAccountPlanStatus(me?.effective_plan_status || me?.plan_status || 'active');
      setSubscriptionUntil(me?.subscription_until || '');
      setBillingStatus(me?.billing_status || 'none');
      setBillingCurrentPeriodEnd(me?.billing_current_period_end || '');
      setBillingCancelAtPeriodEnd(!!me?.billing_cancel_at_period_end);
      setBillingAccessUntil(me?.billing_access_until || '');
      setBillingDeletionScheduledFor(me?.billing_deletion_scheduled_for || '');
      setBillingCustomerId(me?.billing_customer_id || '');
      setEmailNotificationsEnabled(me?.email_notifications_enabled !== false);
      setPremiumSignalEmailsEnabled(me?.premium_signal_emails_enabled !== false);
      setRslEmailDays(Array.isArray(me?.rsl_email_days) ? me.rsl_email_days : []);
      setEmailTheme(me?.email_theme === 'light' ? 'light' : 'dark');
      setPreferredStartView(me?.preferred_start_view || localStorage.getItem(PREFERRED_START_VIEW_STORAGE_KEY) || 'start');
      setTwoFactorEnabled(!!me?.two_factor_enabled);
      setTwoFactorPending(!!me?.two_factor_pending);
      setRiskAcceptedAt(me?.risk_disclaimer_accepted_at || '');
      setRiskAcceptedEmail(me?.risk_disclaimer_accepted_email || '');
      setRiskAcceptedVersion(me?.risk_disclaimer_accepted_version || '');
      setRiskCurrentVersion(me?.risk_disclaimer_current_version || RISK_DISCLAIMER_VERSION);
      setAcquisitionSource(me?.acquisition_source || '');
      setAcquisitionDetail(me?.acquisition_detail || '');
      setAcquisitionAnsweredAt(me?.acquisition_answered_at || '');
      setAcquisitionSkippedAt(me?.acquisition_skipped_at || '');
      setModuleAccess(modules);
      ladeUiSettingsApp();
      ladeBillingConfig();
      ladeBillingHistory();
      setModulesLoaded(true);
      ladeChangelog();
      if (modules.kontoauszug) {
        ladeIbTaxYears();
      }

      if (modules.depot) {
        ladeDepot();
      }
      if (modules.custom_rsl_watchlists) {
        ladeCustomRslWatchlists();
      }
    })();

    return () => {
      mounted = false;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!token || !modulesLoaded) return;
    const isRootEntryPath = window.location.pathname === '/';
    const canAccessCoaching = isAdmin || moduleAccess.coaching;
    const canAccessIndexInvest = isAdmin || moduleAccess.index_invest;
    const views = [
      ...(moduleAccess.start ? ['start'] : []),
      ...(moduleAccess.depot ? ['depot'] : []),
      ...(moduleAccess.depot ? ['rsl_journal'] : []),
      ...(moduleAccess.screener ? ['screener'] : []),
      ...(moduleAccess.rsl_backtest ? ['rsl_backtest'] : []),
      ...(moduleAccess.trade_journal ? ['trade_journal'] : []),
      ...(moduleAccess.kontoauszug ? ['kontoauszug'] : []),
      ...(canAccessCoaching ? ['coaching'] : []),
      ...(moduleAccess.academy ? ['academy'] : []),
      ...(canAccessIndexInvest ? ['index_invest'] : []),
      ...(moduleAccess.support ? ['support'] : []),
      ...(moduleAccess.signals ? ['signals'] : []),
      ...(moduleAccess.konto ? ['konto'] : []),
      ...(isAdmin ? ['admin'] : []),
    ];
    if (views.length > 0 && !views.includes(view)) {
      landingRedirectAppliedRef.current = true;
      navigateToView(views[0], { replace: true });
      return;
    }

    if (!landingRedirectAppliedRef.current) {
      landingRedirectAppliedRef.current = true;
    }

    if (preferredStartRedirectPendingRef.current) {
      preferredStartRedirectPendingRef.current = false;
      if (view === 'start' && preferredStartView !== 'start' && views.includes(preferredStartView)) {
        navigateToView(preferredStartView, { replace: true });
      }
      return;
    }

    if (isRootEntryPath && view === 'start') {
      if (preferredStartView !== 'start' && views.includes(preferredStartView)) {
        navigateToView(preferredStartView, { replace: true });
      } else {
        navigateToView('start', { replace: true });
      }
    }
  }, [token, modulesLoaded, moduleAccess, isAdmin, view, preferredStartView]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!token || !modulesLoaded || view !== 'support' || !moduleAccess.support) return;
    ladeSupportTickets();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, modulesLoaded, view, moduleAccess.support]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!token || !modulesLoaded || view !== 'support' || !moduleAccess.support) return;
    ladeSupportTickets(null, 0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supportStatusFilter]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!token || !modulesLoaded || view !== 'signals' || !moduleAccess.signals) return;
    ladeSignale();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, modulesLoaded, view, moduleAccess.signals]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!token || !modulesLoaded || !moduleAccess.trade_journal) return;
    ladeTradeJournalAccount();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, modulesLoaded, moduleAccess.trade_journal]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!token || !modulesLoaded || view !== 'trade_journal' || !moduleAccess.trade_journal) return;
    setTradeJournalCashTransactions([]);
    setTradeJournalCashTransactionsLoaded(false);
    setTradeJournalCashSummary(null);
    setTradeJournalGroups([]);
    setTradeJournalOverview(null);
    ladeTradeJournalStatementSummary();
    ladeTradeJournalSetups();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, modulesLoaded, view, moduleAccess.trade_journal, tradeJournalSourceFilter]);

  useEffect(() => {
    setTradeJournalLivePnlError('');
  }, [tradeJournalSourceFilter, runApi]);

  useEffect(() => {
    try {
      if (tradeJournalLivePnl && typeof tradeJournalLivePnl === 'object') {
        window.sessionStorage.setItem(TRADE_JOURNAL_LIVE_PNL_STORAGE_KEY, JSON.stringify(tradeJournalLivePnl));
      } else {
        window.sessionStorage.removeItem(TRADE_JOURNAL_LIVE_PNL_STORAGE_KEY);
      }
    } catch {
      // Ignore sessionStorage errors and keep the in-memory snapshot.
    }
  }, [tradeJournalLivePnl]);

  useEffect(() => {
    if (token) return;
    setTradeJournalLivePnl(null);
    try {
      window.sessionStorage.removeItem(TRADE_JOURNAL_LIVE_PNL_STORAGE_KEY);
    } catch {
      // Ignore sessionStorage errors on logout cleanup.
    }
  }, [token]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!token || !modulesLoaded || !tradeJournalSyncJobId) return;
    tradeJournalSyncPollTimeoutRef.current = setTimeout(() => {
      ladeTradeJournalSyncStatus(tradeJournalSyncJobId);
    }, 1500);
    return () => {
      if (tradeJournalSyncPollTimeoutRef.current) {
        clearTimeout(tradeJournalSyncPollTimeoutRef.current);
        tradeJournalSyncPollTimeoutRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, modulesLoaded, tradeJournalSyncJobId]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const enteredScreener = previousViewRef.current !== 'screener' && view === 'screener';
    previousViewRef.current = view;
    const needsInitialScreenerLoad = view === 'screener' && ergebnis === null && !statusMsg;
    if ((!enteredScreener && !needsInitialScreenerLoad) || !token || !modulesLoaded || !moduleAccess.screener || laden) return;
    analyseAll();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, modulesLoaded, view, moduleAccess.screener, laden, ergebnis, statusMsg]);

  useEffect(() => {
    setSignalOpenLimit(8);
    setSignalCloseLimit(8);
  }, [signalsEntries, signalFilter, signalViewMode]);

  async function ladeCurrentUser() {
    try {
      return await apiRequest('/me');
    } catch (err) {
      if (handleSessionError(err) || !getStoredAuth().sessionActive) return null;
      return null;
    }
  }

  async function ladeModuleRechte() {
    try {
      const data = await apiRequest('/me/modules');
      return {
        start: !!data.start,
        konto: !!data.konto,
        screener: !!data.screener,
        rsl_backtest: !!data.rsl_backtest,
        depot: !!data.depot,
        trade_journal: !!data.trade_journal,
        kontoauszug: !!data.kontoauszug,
        coaching: !!data.coaching,
        academy: !!data.academy,
        support: !!data.support,
        signals: !!data.signals,
        index_invest: !!data.index_invest,
        custom_rsl_watchlists: !!data.custom_rsl_watchlists,
      };
    } catch (err) {
      if (handleSessionError(err) || !getStoredAuth().sessionActive) {
        return { start: false, konto: false, screener: false, rsl_backtest: false, depot: false, trade_journal: false, kontoauszug: false, coaching: false, academy: false, support: false, signals: false, index_invest: false, custom_rsl_watchlists: false };
      }
      return { start: false, konto: false, screener: false, rsl_backtest: false, depot: false, trade_journal: false, kontoauszug: false, coaching: false, academy: false, support: false, signals: false, index_invest: false, custom_rsl_watchlists: false };
    }
  }

  async function ladeTradeJournalAccount() {
    setTradeJournalErr('');
    setTradeJournalLoading(true);
    try {
      const data = await apiRequest('/trade-journal/account');
      applyTradeJournalAccountPayload(data);
    } catch (err) {
      if (handleSessionError(err)) return;
      setTradeJournalErr(err?.message || 'Trade-Journal-Konfiguration konnte nicht geladen werden');
    } finally {
      setTradeJournalLoading(false);
    }
  }

  const ladeTradeJournalGroups = useCallback(async (params = {}) => {
    setTradeJournalSyncError('');
    await runApi(
      async () => {
        const pageSize = 500;
        let offset = 0;
        let total = null;
        const mergedItems = [];

        while (total === null || offset < total) {
          const searchParams = new URLSearchParams();
          if (tradeJournalSourceFilter && tradeJournalSourceFilter !== 'all') {
            searchParams.set('source', tradeJournalSourceFilter);
          }
          searchParams.set('limit', String(pageSize));
          searchParams.set('offset', String(offset));
          if (params.from) searchParams.set('from_date', params.from);
          if (params.to) searchParams.set('to_date', params.to);
          if (params.dateBasis) searchParams.set('date_basis', params.dateBasis);
          if (params.status && params.status !== 'ALL') searchParams.set('status', params.status);
          if (params.instrument && params.instrument !== 'ALL') searchParams.set('instrument', params.instrument);
          if (params.symbol) searchParams.set('symbol', params.symbol);
          if (params.exactSymbol) searchParams.set('exact_symbol', 'true');
          if (params.setupId && params.setupId !== 'ALL') searchParams.set('setup_id', String(params.setupId));
          if (params.sortKey) searchParams.set('sort_key', params.sortKey);
          if (params.sortDirection) searchParams.set('sort_direction', params.sortDirection);
          if (params.prioritizeOpen) searchParams.set('prioritize_open', 'true');
          const query = searchParams.toString();
          const data = await apiRequest(`/trade-journal/groups-page${query ? `?${query}` : ''}`, { timeoutMs: TRADE_JOURNAL_REQUEST_TIMEOUT_MS });
          const items = Array.isArray(data?.items) ? data.items : [];
          total = Number.isFinite(Number(data?.total)) ? Number(data.total) : items.length;
          mergedItems.push(...items);
          if (!items.length || items.length < pageSize) {
            break;
          }
          offset += items.length;
        }

        setTradeJournalGroups(mergedItems);
        setTradeJournalSyncError('');
      },
      {
        defaultValue: null,
        onError: message => setTradeJournalSyncError(message || 'Trade-Journal-Gruppen konnten nicht geladen werden'),
      }
    );
  }, [tradeJournalSourceFilter, runApi]);

  const ladeTradeJournalGroupsPage = useCallback(async (params = {}) => {
    setTradeJournalGroupPageLoading(true);
    await runApi(
      async () => {
        const searchParams = new URLSearchParams();
        if (tradeJournalSourceFilter && tradeJournalSourceFilter !== 'all') {
          searchParams.set('source', tradeJournalSourceFilter);
        }
        const normalizedLimit = Math.max(1, Math.min(Number(params.limit || 50), 500));
        const normalizedOffset = Math.max(0, Number(params.offset || 0));
        searchParams.set('limit', String(normalizedLimit));
        searchParams.set('offset', String(normalizedOffset));
        if (params.from) searchParams.set('from_date', params.from);
        if (params.to) searchParams.set('to_date', params.to);
        if (params.dateBasis) searchParams.set('date_basis', params.dateBasis);
        if (params.status && params.status !== 'ALL') searchParams.set('status', params.status);
        if (params.instrument && params.instrument !== 'ALL') searchParams.set('instrument', params.instrument);
        if (params.symbol) searchParams.set('symbol', params.symbol);
        if (params.exactSymbol) searchParams.set('exact_symbol', 'true');
        if (params.setupId && params.setupId !== 'ALL') searchParams.set('setup_id', String(params.setupId));
        if (params.sortKey) searchParams.set('sort_key', params.sortKey);
        if (params.sortDirection) searchParams.set('sort_direction', params.sortDirection);
        if (params.prioritizeOpen) searchParams.set('prioritize_open', 'true');
        const query = searchParams.toString();
        const data = await apiRequest(`/trade-journal/groups-page${query ? `?${query}` : ''}`, { timeoutMs: TRADE_JOURNAL_REQUEST_TIMEOUT_MS });
        setTradeJournalGroupPage(data && typeof data === 'object'
          ? data
          : { items: [], total: 0, limit: normalizedLimit, offset: normalizedOffset, cache_hit: false, cached_at: null });
        setTradeJournalSyncError('');
      },
      {
        defaultValue: null,
        onError: message => setTradeJournalSyncError(message || 'Trade-Journal-Tabelle konnte nicht geladen werden'),
      }
    );
    setTradeJournalGroupPageLoading(false);
  }, [tradeJournalSourceFilter, runApi]);

  const ladeTradeJournalOverview = useCallback(async (params = {}) => {
    setTradeJournalOverviewLoading(true);
    await runApi(
      async () => {
        const searchParams = new URLSearchParams();
        if (tradeJournalSourceFilter && tradeJournalSourceFilter !== 'all') {
          searchParams.set('source', tradeJournalSourceFilter);
        }
        if (params.from) searchParams.set('from_date', params.from);
        if (params.to) searchParams.set('to_date', params.to);
        if (params.dateBasis) searchParams.set('date_basis', params.dateBasis);
        if (params.status && params.status !== 'ALL') searchParams.set('status', params.status);
        if (params.instrument && params.instrument !== 'ALL') searchParams.set('instrument', params.instrument);
        if (params.symbol) searchParams.set('symbol', params.symbol);
        if (params.exactSymbol) searchParams.set('exact_symbol', 'true');
        if (params.setupId && params.setupId !== 'ALL') searchParams.set('setup_id', String(params.setupId));
        const query = searchParams.toString();
        const data = await apiRequest(`/trade-journal/overview${query ? `?${query}` : ''}`, { timeoutMs: TRADE_JOURNAL_REQUEST_TIMEOUT_MS });
        setTradeJournalOverview(data && typeof data === 'object' ? data : null);
        setTradeJournalSyncError('');
      },
      {
        defaultValue: null,
        onError: message => setTradeJournalSyncError(message || 'Trade-Journal-Übersicht konnte nicht geladen werden'),
      }
    );
    setTradeJournalOverviewLoading(false);
  }, [tradeJournalSourceFilter, runApi]);

  const ladeTradeJournalCashSummary = useCallback(async (params = {}) => {
    await runApi(
      async () => {
        const searchParams = new URLSearchParams();
        if (tradeJournalSourceFilter && tradeJournalSourceFilter !== 'all') {
          searchParams.set('source', tradeJournalSourceFilter);
        }
        if (params.from) searchParams.set('from_date', params.from);
        if (params.to) searchParams.set('to_date', params.to);
        if (params.symbol) searchParams.set('symbol', params.symbol);
        if (params.exactSymbol) searchParams.set('exact_symbol', 'true');
        const query = searchParams.toString();
        const data = await apiRequest(`/trade-journal/cash-summary${query ? `?${query}` : ''}`, { timeoutMs: TRADE_JOURNAL_REQUEST_TIMEOUT_MS });
        setTradeJournalCashSummary(data && typeof data === 'object' ? data : null);
        setTradeJournalSyncError('');
      },
      {
        defaultValue: null,
        onError: () => setTradeJournalCashSummary(null),
      }
    );
  }, [tradeJournalSourceFilter, runApi]);

  const invalidateTradeJournalDashboard = useCallback(() => {
    tradeJournalDashboardRequestRef.current += 1;
    setTradeJournalOverviewLoading(false);
    setTradeJournalGroupPageLoading(false);
  }, []);

  const ladeTradeJournalDashboard = useCallback(async (params = {}) => {
    const requestId = ++tradeJournalDashboardRequestRef.current;
    const isCurrentRequest = () => requestId === tradeJournalDashboardRequestRef.current;
    const normalizedLimit = Math.max(1, Math.min(Number(params.limit || 50), 500));
    const normalizedOffset = Math.max(0, Number(params.offset || 0));
    setTradeJournalOverviewLoading(true);
    setTradeJournalGroupPageLoading(true);
    await runApi(
      async () => {
        const searchParams = new URLSearchParams();
        if (tradeJournalSourceFilter && tradeJournalSourceFilter !== 'all') {
          searchParams.set('source', tradeJournalSourceFilter);
        }
        searchParams.set('limit', String(normalizedLimit));
        searchParams.set('offset', String(normalizedOffset));
        if (params.from) searchParams.set('from_date', params.from);
        if (params.to) searchParams.set('to_date', params.to);
        if (params.dateBasis) searchParams.set('date_basis', params.dateBasis);
        if (params.status && params.status !== 'ALL') searchParams.set('status', params.status);
        if (params.instrument && params.instrument !== 'ALL') searchParams.set('instrument', params.instrument);
        if (params.symbol) searchParams.set('symbol', params.symbol);
        if (params.exactSymbol) searchParams.set('exact_symbol', 'true');
        if (params.setupId && params.setupId !== 'ALL') searchParams.set('setup_id', String(params.setupId));
        if (params.sortKey) searchParams.set('sort_key', params.sortKey);
        if (params.sortDirection) searchParams.set('sort_direction', params.sortDirection);
        if (params.prioritizeOpen) searchParams.set('prioritize_open', 'true');
        const query = searchParams.toString();
        let data;
        try {
          data = await apiRequest(`/trade-journal/dashboard${query ? `?${query}` : ''}`, { timeoutMs: TRADE_JOURNAL_REQUEST_TIMEOUT_MS });
        } catch (err) {
          if (!isCurrentRequest()) return;
          throw err;
        }
        if (!isCurrentRequest()) return;
        setTradeJournalOverview(data?.overview && typeof data.overview === 'object' ? data.overview : null);
        setTradeJournalCashSummary(data?.cash_summary && typeof data.cash_summary === 'object' ? data.cash_summary : null);
        setTradeJournalGroupPage(data?.group_page && typeof data.group_page === 'object'
          ? data.group_page
          : { items: [], total: 0, limit: normalizedLimit, offset: normalizedOffset, cache_hit: false, cached_at: null });
        setTradeJournalSyncError('');
      },
      {
        defaultValue: null,
        onError: message => {
          if (!isCurrentRequest()) return;
          setTradeJournalSyncError(`Trade-Journal-Ansicht konnte nicht aktualisiert werden: ${message || 'Laden fehlgeschlagen'}`);
        },
      }
    );
    if (isCurrentRequest()) {
      setTradeJournalOverviewLoading(false);
      setTradeJournalGroupPageLoading(false);
    }
  }, [tradeJournalSourceFilter, runApi]);

  const ladeTradeJournalCashTransactions = useCallback(async (params = {}) => {
    await runApi(
      async () => {
        const searchParams = new URLSearchParams();
        if (tradeJournalSourceFilter && tradeJournalSourceFilter !== 'all') {
          searchParams.set('source', tradeJournalSourceFilter);
        }
        if (params.from) searchParams.set('from_date', params.from);
        if (params.to) searchParams.set('to_date', params.to);
        if (params.symbol) searchParams.set('symbol', params.symbol);
        if (params.exactSymbol) searchParams.set('exact_symbol', 'true');
        const query = searchParams.toString();
        const data = await apiRequest(`/trade-journal/cash-transactions${query ? `?${query}` : ''}`, { timeoutMs: TRADE_JOURNAL_REQUEST_TIMEOUT_MS });
        setTradeJournalCashTransactions(Array.isArray(data) ? data : []);
        setTradeJournalCashTransactionsLoaded(true);
      },
      { defaultValue: null, onError: () => {} }
    );
  }, [tradeJournalSourceFilter, runApi]);

  async function ladeTradeJournalStatementSummary() {
    const query = tradeJournalSourceFilter && tradeJournalSourceFilter !== 'all'
      ? `?source=${encodeURIComponent(tradeJournalSourceFilter)}`
      : '';
    await runApi(
      async () => {
        const data = await apiRequest(`/trade-journal/statement-summary${query}`, { timeoutMs: TRADE_JOURNAL_REQUEST_TIMEOUT_MS });
        setTradeJournalStatementSummary(data && typeof data === 'object' ? data : null);
      },
      {
        defaultValue: null,
        onError: () => setTradeJournalStatementSummary(null),
      }
    );
  }

  async function ladeTradeJournalSetups() {
    await runApi(
      async () => {
        const data = await apiRequest('/trade-journal/setups', { timeoutMs: TRADE_JOURNAL_REQUEST_TIMEOUT_MS });
        setTradeJournalSetups(Array.isArray(data) ? data : []);
      },
      { defaultValue: null, onError: () => setTradeJournalSetups([]) }
    );
  }

  async function aktualisiereTradeJournalLivePnl(params = {}) {
    const searchParams = new URLSearchParams();
    if (tradeJournalSourceFilter && tradeJournalSourceFilter !== 'all') {
      searchParams.set('source', tradeJournalSourceFilter);
    }
    if (params.from) searchParams.set('from_date', params.from);
    if (params.to) searchParams.set('to_date', params.to);
    if (params.dateBasis) searchParams.set('date_basis', params.dateBasis);
    if (params.status && params.status !== 'ALL') searchParams.set('status', params.status);
    if (params.instrument && params.instrument !== 'ALL') searchParams.set('instrument', params.instrument);
    if (params.symbol) searchParams.set('symbol', params.symbol);
    if (params.exactSymbol) searchParams.set('exact_symbol', 'true');
    if (params.setupId && params.setupId !== 'ALL') searchParams.set('setup_id', String(params.setupId));
    const query = searchParams.toString();
    setTradeJournalLivePnlLoading(true);
    setTradeJournalLivePnlError('');
    try {
      if (tradeJournalAgentSecretSet) {
        await requestTradeJournalAgentRefreshAndWait();
      }
      const data = await apiRequest(`/trade-journal/live-pnl${query ? `?${query}` : ''}`, {
        method: 'POST',
        timeoutMs: TRADE_JOURNAL_LIVE_PNL_TIMEOUT_MS,
      });
      setTradeJournalLivePnl(data && typeof data === 'object' ? data : null);
    } catch (err) {
      if (handleSessionError(err)) return;
      setTradeJournalLivePnlError(err?.message || 'Live P&L konnte nicht aktualisiert werden');
    } finally {
      setTradeJournalLivePnlLoading(false);
    }
  }

  async function tradeJournalSetupAnlegen(payload) {
    const data = await apiRequest('/trade-journal/setups', {
      method: 'POST',
      body: payload,
    });
    await ladeTradeJournalSetups();
    return data;
  }

  async function tradeJournalSetupBearbeiten(setupId, payload) {
    const data = await apiRequest(`/trade-journal/setups/${setupId}`, {
      method: 'PATCH',
      body: payload,
    });
    await ladeTradeJournalSetups();
    return data;
  }

  async function tradeJournalSetupLoeschen(setupId) {
    const data = await apiRequest(`/trade-journal/setups/${setupId}`, {
      method: 'DELETE',
    });
    await ladeTradeJournalSetups();
    return data;
  }

  async function tradeJournalGroupSetupsSpeichern(groupKey, setupIds) {
    return await apiRequest(`/trade-journal/groups/${encodeURIComponent(groupKey)}/setups`, {
      method: 'PUT',
      body: { setup_ids: Array.isArray(setupIds) ? setupIds : [] },
      timeoutMs: TRADE_JOURNAL_REQUEST_TIMEOUT_MS,
    });
  }

  async function tradeJournalBulkGroupSetupsSpeichern(groupKeys, setupIds) {
    return await apiRequest('/trade-journal/groups/setups/bulk', {
      method: 'PUT',
      timeoutMs: TRADE_JOURNAL_REQUEST_TIMEOUT_MS,
      body: {
        group_keys: Array.isArray(groupKeys) ? groupKeys : [],
        setup_ids: Array.isArray(setupIds) ? setupIds : [],
      },
    });
  }

  async function tradeJournalGruppeBearbeiten(groupKey, fills) {
    return await apiRequest(`/trade-journal/groups/${encodeURIComponent(groupKey)}`, {
      method: 'PUT',
      body: { fills: Array.isArray(fills) ? fills : [] },
    });
  }

  async function tradeJournalGruppeLoeschen(groupKey) {
    return await apiRequest(`/trade-journal/groups/${encodeURIComponent(groupKey)}`, {
      method: 'DELETE',
    });
  }

  async function tradeJournalManuelleEintraegeAnlegen(fills) {
    return await apiRequest('/trade-journal/manual-fills', {
      method: 'POST',
      body: { fills: Array.isArray(fills) ? fills : [] },
      timeoutMs: TRADE_JOURNAL_REQUEST_TIMEOUT_MS,
    });
  }

  async function ladeTradeJournalSyncStatus(jobId) {
    if (tradeJournalSyncPollTimeoutRef.current) {
      clearTimeout(tradeJournalSyncPollTimeoutRef.current);
      tradeJournalSyncPollTimeoutRef.current = null;
    }
    await runApi(
      async () => {
        const data = await apiRequest(`/trade-journal/sync/${jobId}`);
        setTradeJournalSyncStatus(data.status || '');
        setTradeJournalSyncProgress(Number.isFinite(data.progress) ? data.progress : 0);
        setTradeJournalSyncMessage(data.message || '');
        setTradeJournalSyncFetchedCount(data.fetched_count || 0);
        setTradeJournalSyncImportedCount(data.imported_count || 0);
        setTradeJournalSyncSkippedCount(data.skipped_count || 0);
        setTradeJournalSyncImportedItems(Array.isArray(data.imported_items) ? data.imported_items : []);
        setTradeJournalSyncError(data.error || '');
        if (data.status === 'done') {
          setTradeJournalLastSyncImportedCount(data.imported_count || 0);
          setTradeJournalLastSyncSkippedCount(data.skipped_count || 0);
          setTradeJournalLastSyncItems(Array.isArray(data.imported_items) ? data.imported_items : []);
          setTradeJournalSyncLoading(false);
          setTradeJournalGroups([]);
          setTradeJournalOverview(null);
          setTradeJournalCashSummary(null);
          setTradeJournalCashTransactions([]);
          setTradeJournalCashTransactionsLoaded(false);
          ladeTradeJournalStatementSummary();
          ladeTradeJournalSetups();
          ladeTradeJournalAccount();
        } else if (data.status === 'failed') {
          setTradeJournalSyncLoading(false);
          ladeTradeJournalAccount();
        } else {
          tradeJournalSyncPollTimeoutRef.current = setTimeout(() => {
            ladeTradeJournalSyncStatus(jobId);
          }, 1500);
        }
      },
      {
        defaultValue: null,
        onError: message => {
          setTradeJournalSyncLoading(false);
          setTradeJournalSyncError(message || 'Trade-Journal-Syncstatus konnte nicht geladen werden');
        },
      }
    );
  }

  async function ladeDepot() {
    setDepotLaden(true);
    await runApi(
      async () => {
        const data = await apiRequest('/depot/');
        setDepot(data);
        const historyData = await apiRequest('/depot/historie');
        setHistorie(historyData);
      },
      { defaultValue: null }
    );
    setDepotLaden(false);
  }

  async function ladeCustomRslWatchlists() {
    setCustomRslWatchlistsLoading(true);
    setCustomRslWatchlistsError('');
    try {
      const data = await apiRequest('/custom-rsl-watchlists');
      setCustomRslWatchlists(Array.isArray(data.watchlists) ? data.watchlists : []);
    } catch (err) {
      if (!handleSessionError(err)) {
        setCustomRslWatchlistsError(err?.message || 'Eigene RSL-Watchlisten konnten nicht geladen werden');
      }
    } finally {
      setCustomRslWatchlistsLoading(false);
    }
  }

  async function createCustomRslWatchlist(payload) {
    setCustomRslWatchlistsError('');
    try {
      await apiRequest('/custom-rsl-watchlists', { method: 'POST', body: payload });
      await ladeCustomRslWatchlists();
    } catch (err) {
      setCustomRslWatchlistsError(err?.message || 'Watchlist konnte nicht angelegt werden');
    }
  }

  async function updateCustomRslWatchlist(id, payload) {
    setCustomRslWatchlistsError('');
    try {
      await apiRequest(`/custom-rsl-watchlists/${id}`, { method: 'PATCH', body: payload });
      await ladeCustomRslWatchlists();
      await ladeDepot();
    } catch (err) {
      setCustomRslWatchlistsError(err?.message || 'Watchlist konnte nicht gespeichert werden');
    }
  }

  async function deleteCustomRslWatchlist(id) {
    setCustomRslWatchlistsError('');
    try {
      await apiRequest(`/custom-rsl-watchlists/${id}`, { method: 'DELETE' });
      await ladeCustomRslWatchlists();
      await ladeDepot();
    } catch (err) {
      setCustomRslWatchlistsError(err?.message || 'Watchlist konnte nicht gelöscht werden');
    }
  }

  async function addCustomRslWatchlistItem(id, payload) {
    setCustomRslWatchlistsError('');
    try {
      await apiRequest(`/custom-rsl-watchlists/${id}/items/bulk`, { method: 'POST', body: payload });
      await ladeCustomRslWatchlists();
      await ladeDepot();
      return { ok: true };
    } catch (err) {
      const detail = err instanceof ApiError ? err?.payload?.detail : null;
      if (
        err instanceof ApiError
        && err.status === 409
        && detail
        && typeof detail === 'object'
        && detail.code === 'multiple_symbol_matches'
        && Array.isArray(detail.ambiguities)
      ) {
        return { ok: false, ambiguities: detail.ambiguities };
      }
      setCustomRslWatchlistsError(err?.message || 'Titel konnte nicht hinzugefügt werden');
      return { ok: false, ambiguities: [] };
    }
  }

  async function runCustomRslWatchlists() {
    setCustomRslWatchlistsError('');
    try {
      const data = await apiRequest('/custom-rsl-watchlists/run', { method: 'POST' });
      await ladeCustomRslWatchlists();
      await ladeDepot();
      return { ok: true, run: data?.run || null };
    } catch (err) {
      setCustomRslWatchlistsError(err?.message || 'Watchlist-Lauf konnte nicht gestartet werden');
      return { ok: false };
    }
  }

  async function reportCustomRslWatchlistIssues(payload) {
    setCustomRslWatchlistsError('');
    try {
      const detail = await apiRequest('/support/tickets', {
        method: 'POST',
        body: payload,
      });
      return { ok: true, ticketId: detail?.id };
    } catch (err) {
      setCustomRslWatchlistsError(err?.message || 'Fehlerhafte Titel konnten nicht gemeldet werden');
      return { ok: false };
    }
  }

  async function previewTradingViewWatchlist(sourceUrl) {
    setCustomRslWatchlistsError('');
    try {
      const data = await apiRequest('/custom-rsl-watchlists/import/tradingview/preview', {
        method: 'POST',
        body: { source_url: sourceUrl },
      });
      return { ok: true, data };
    } catch (err) {
      setCustomRslWatchlistsError(err?.message || 'TradingView-Watchlist konnte nicht gelesen werden');
      return { ok: false, data: null };
    }
  }

  async function importTradingViewWatchlist(payload) {
    setCustomRslWatchlistsError('');
    try {
      const data = await apiRequest('/custom-rsl-watchlists/import/tradingview', {
        method: 'POST',
        body: payload,
      });
      await ladeCustomRslWatchlists();
      await ladeDepot();
      return { ok: true, autoRun: data?.auto_run || null };
    } catch (err) {
      setCustomRslWatchlistsError(err?.message || 'TradingView-Watchlist konnte nicht importiert werden');
      return { ok: false };
    }
  }

  async function deleteCustomRslWatchlistItem(id, itemId) {
    setCustomRslWatchlistsError('');
    try {
      await apiRequest(`/custom-rsl-watchlists/${id}/items/${itemId}`, { method: 'DELETE' });
      await ladeCustomRslWatchlists();
      await ladeDepot();
    } catch (err) {
      setCustomRslWatchlistsError(err?.message || 'Titel konnte nicht entfernt werden');
    }
  }

  async function ladeUiSettingsApp() {
    await runApi(
      async () => {
        const data = await apiRequest('/app/ui-settings');
        setUiSettings({
          tradingview_charts_enabled: !!data.tradingview_charts_enabled,
        });
      },
      {
        defaultValue: null,
        onError: () => setUiSettings({ tradingview_charts_enabled: false }),
        fallbackMessage: '',
      }
    );
  }

  async function ladeBillingConfig() {
    return runApi(
      async () => {
        const data = await apiRequest('/app/billing-config');
        setBillingConfig({
          billing_enabled: !!data.billing_enabled,
          provider: data.provider || 'stripe',
          portal_enabled: !!data.portal_enabled,
          basic_monthly_available: !!data.basic_monthly_available,
          basic_yearly_available: !!data.basic_yearly_available,
          premium_monthly_available: !!data.premium_monthly_available,
          premium_yearly_available: !!data.premium_yearly_available,
          coaching_available: !!data.coaching_available,
        });
      },
      {
        fallbackMessage: 'Billing-Konfiguration konnte nicht geladen werden',
      }
    );
  }

  async function ladeBillingHistory() {
    return runApi(
      async () => {
        const data = await apiRequest('/me/billing-history');
        setBillingHistory(Array.isArray(data) ? data : []);
      },
      {
        defaultValue: [],
        onError: () => setBillingHistory([]),
        fallbackMessage: '',
      }
    );
  }

  async function ladeSupportTickets(selectTicketId = null, nextOffset = supportTicketOffset) {
    setSupportLoading(true);
    setSupportErr('');
    try {
      await runApi(
        async () => {
          const safeOffset = Math.max(0, Number(nextOffset) || 0);
          const params = new URLSearchParams({
            limit: String(supportTicketLimit),
            offset: String(safeOffset),
            status_filter: supportStatusFilter,
          });
          const data = await apiRequest(`/support/tickets?${params.toString()}`);
          const items = Array.isArray(data?.items) ? data.items : [];
          setSupportTickets(items);
          setSupportTicketTotal(Number.isFinite(data?.total) ? data.total : items.length);
          setSupportTicketOffset(Number.isFinite(data?.offset) ? data.offset : safeOffset);

          const preferredId = selectTicketId || supportDetail?.id;
          const visibleTargetId = preferredId && items.some(ticket => ticket.id === preferredId) ? preferredId : null;
          if (visibleTargetId) {
            const detail = await apiRequest(`/support/tickets/${visibleTargetId}`);
            setSupportDetail(detail);
            setSupportStatusValue(detail.status);
          } else if (items.length > 0) {
            const detail = await apiRequest(`/support/tickets/${items[0].id}`);
            setSupportDetail(detail);
            setSupportStatusValue(detail.status);
          } else {
            setSupportDetail(null);
            setSupportStatusValue('offen');
            setSupportDeleteConfirmText('');
          }
        },
        { onError: setSupportErr, fallbackMessage: 'Support-Tickets konnten nicht geladen werden' }
      );
    } finally {
      setSupportLoading(false);
    }
  }

  async function ladeSupportTicketDetail(ticketId) {
    setSupportErr('');
    await runApi(
      async () => {
        const detail = await apiRequest(`/support/tickets/${ticketId}`);
        setSupportDetail(detail);
        setSupportStatusValue(detail.status);
        setSupportDeleteConfirmText('');
      },
      { onError: setSupportErr, fallbackMessage: 'Ticket konnte nicht geladen werden' }
    );
  }

  async function supportTicketErstellen() {
    setSupportErr('');
    setSupportMsg('');
    await runApi(
      async () => {
        const detail = await apiRequest('/support/tickets', {
          method: 'POST',
          body: {
            subject: supportSubject,
            category: supportCategory,
            message: supportMessage,
          },
        });
        setSupportSubject('');
        setSupportCategory('Allgemeine Frage');
        setSupportMessage('');
        setSupportReply('');
        setSupportDeleteConfirmText('');
        setSupportMsg('Ticket wurde erstellt');
        await ladeSupportTickets(detail.id);
      },
      { onError: setSupportErr, fallbackMessage: 'Ticket konnte nicht erstellt werden' }
    );
  }

  async function supportAntwortSenden() {
    if (!supportDetail) return;
    setSupportErr('');
    setSupportMsg('');
    await runApi(
      async () => {
        const detail = await apiRequest(`/support/tickets/${supportDetail.id}/messages`, {
          method: 'POST',
          body: { message: supportReply },
        });
        setSupportReply('');
        setSupportDetail(detail);
        setSupportStatusValue(detail.status);
        setSupportDeleteConfirmText('');
        setSupportMsg('Nachricht wurde gesendet');
        await ladeSupportTickets(detail.id);
      },
      { onError: setSupportErr, fallbackMessage: 'Nachricht konnte nicht gesendet werden' }
    );
  }

  async function supportStatusSpeichern() {
    if (!supportDetail || !isAdmin) return;
    setSupportErr('');
    setSupportMsg('');
    await runApi(
      async () => {
        const detail = await apiRequest(`/support/tickets/${supportDetail.id}`, {
          method: 'PATCH',
          body: { status: supportStatusValue },
        });
        setSupportDetail(detail);
        setSupportDeleteConfirmText('');
        setSupportMsg('Status gespeichert');
        await ladeSupportTickets(
          supportStatusFilter === 'closed' || detail.status !== 'geschlossen' ? detail.id : null,
          supportStatusFilter === 'closed' || detail.status !== 'geschlossen' ? supportTicketOffset : 0
        );
      },
      { onError: setSupportErr, fallbackMessage: 'Status konnte nicht gespeichert werden' }
    );
  }

  async function supportTicketLoeschen() {
    if (!supportDetail) return;
    setSupportErr('');
    setSupportMsg('');
    await runApi(
      async () => {
        await apiRequest(`/support/tickets/${supportDetail.id}`, {
          method: 'DELETE',
          body: { confirm_text: supportDeleteConfirmText },
        });
        setSupportDeleteConfirmText('');
        setSupportReply('');
        setSupportDetail(null);
        setSupportStatusValue('offen');
        setSupportMsg('Ticket wurde gelöscht');
        await ladeSupportTickets(null, 0);
      },
      { onError: setSupportErr, fallbackMessage: 'Ticket konnte nicht gelöscht werden' }
    );
  }

  async function supportTicketSchliessen() {
    if (!supportDetail || isAdmin) return;
    setSupportErr('');
    setSupportMsg('');
    await runApi(
      async () => {
        const detail = await apiRequest(`/support/tickets/${supportDetail.id}`, {
          method: 'PATCH',
          body: { status: 'geschlossen' },
        });
        setSupportDetail(detail);
        setSupportStatusValue(detail.status);
        setSupportDeleteConfirmText('');
        setSupportMsg('Ticket wurde geschlossen');
        await ladeSupportTickets(
          supportStatusFilter === 'closed' || detail.status !== 'geschlossen' ? detail.id : null,
          supportStatusFilter === 'closed' || detail.status !== 'geschlossen' ? supportTicketOffset : 0
        );
      },
      { onError: setSupportErr, fallbackMessage: 'Ticket konnte nicht geschlossen werden' }
    );
  }

  async function ladeSignale() {
    setSignalsLoading(true);
    setSignalsErr('');
    try {
      await runApi(
        async () => {
          const [data, sentiment, marketSignals] = await Promise.all([
            apiRequest('/app/signals'),
            apiRequest('/app/signals/market-sentiment').catch(() => null),
            apiRequest('/app/signals/market-signals').catch(() => []),
          ]);
          setSignalsEntries(Array.isArray(data) ? data : []);
          setMarketSentiment(sentiment && typeof sentiment === 'object' ? sentiment : null);
          setMarketSignalCards(Array.isArray(marketSignals) ? marketSignals : []);
        },
        { onError: setSignalsErr, fallbackMessage: 'Signale konnten nicht geladen werden' }
      );
    } finally {
      setSignalsLoading(false);
    }
  }

  async function signalSpeichern() {
    setSignalsErr('');
    setSignalsMsg('');
    await runApi(
      async () => {
        const data = signalEditId
          ? await apiRequest(`/admin/signals/${signalEditId}`, {
              method: 'PATCH',
              body: { text: signalText },
            })
          : await apiRequest('/admin/signals', {
              method: 'POST',
              body: {
                kind: signalKind,
                text: signalText,
                related_signal_id: signalKind === 'close' ? (signalRelatedId || null) : null,
              },
            });
        const entries = Array.isArray(data) ? data : (Array.isArray(data?.entries) ? data.entries : []);
        setSignalsEntries(entries);
        setSignalText('');
        setSignalKind('open');
        setSignalRelatedId('');
        setSignalEditId(null);
        setSignalsMsg(
          signalEditId
            ? 'Signal aktualisiert'
            : `Signal gespeichert${typeof data?.recipient_count === 'number' ? `, E-Mail an ${data.recipient_count} Plus-User` : ''}`
        );
      },
      { onError: setSignalsErr, fallbackMessage: 'Signal konnte nicht gespeichert werden' }
    );
  }

  async function marketSignalAlarmAendern(signalKey, alertEnabled) {
    setSignalsErr('');
    setSignalsMsg('');
    setMarketSignalsErr('');
    setMarketSignalsMsg('');
    if (alertEnabled && !(isAdmin || accountPlan === 'premium')) {
      setMarketSignalsErr('Alarm per E-Mail ist nur mit Plus verfügbar.');
      showPlusUpgradeHint('Alarm per E-Mail');
      return;
    }
    const previous = marketSignalCards;
    setMarketSignalCards(cards => cards.map(card => (
      card.key === signalKey ? { ...card, alert_enabled: alertEnabled } : card
    )));
    await runApi(
      async () => {
        const data = await apiRequest(`/app/signals/market-signals/${encodeURIComponent(signalKey)}`, {
          method: 'PATCH',
          body: { alert_enabled: alertEnabled },
        });
        setMarketSignalCards(Array.isArray(data) ? data : []);
        setMarketSignalsMsg(alertEnabled ? 'Alarm aktiviert' : 'Alarm deaktiviert');
      },
      {
        onError: err => {
          setMarketSignalCards(previous);
          setMarketSignalsErr(err);
        },
        fallbackMessage: 'Alarm konnte nicht gespeichert werden',
      }
    );
  }

  async function signalLoeschen(signalId) {
    setSignalsErr('');
    setSignalsMsg('');
    if (!(await requestConfirm('Signal wirklich löschen?'))) return;
    await runApi(
      async () => {
        const data = await apiRequest(`/admin/signals/${signalId}`, {
          method: 'DELETE',
        });
        setSignalsEntries(Array.isArray(data) ? data : []);
        if (signalEditId === signalId) {
          setSignalEditId(null);
          setSignalText('');
          setSignalKind('open');
          setSignalRelatedId('');
        }
        setSignalsMsg('Signal gelöscht');
      },
      { onError: setSignalsErr, fallbackMessage: 'Signal konnte nicht gelöscht werden' }
    );
  }

  function signalBearbeiten(entry) {
    setSignalEditId(entry.id);
    setSignalKind(entry.kind);
    setSignalText(entry.text);
    setSignalRelatedId(entry.related_signal_id || '');
    setSignalsMsg('');
    setSignalsErr('');
  }

  async function ladeChangelog() {
    await runApi(
      async () => {
        const data = await apiRequest('/app/changelog');
        setChangelogEntries(Array.isArray(data) ? data : []);
      },
      {
        defaultValue: null,
        onError: () => setChangelogEntries([]),
        fallbackMessage: '',
      }
    );
  }

  async function speicherePreferredStartView(nextView) {
    setPreferredStartView(nextView);
    localStorage.setItem(PREFERRED_START_VIEW_STORAGE_KEY, nextView);
    setPreferredStartViewMsg('');
    setPreferredStartViewErr('');
    await runApi(
      async () => {
        const data = await apiRequest('/me/preferences', {
          method: 'PATCH',
          body: { preferred_start_view: nextView },
        });
        setPreferredStartView(data?.preferred_start_view || nextView);
        setPreferredStartViewMsg('Startseite gespeichert');
        setTimeout(() => setPreferredStartViewMsg(''), 2500);
      },
      {
        onError: message => setPreferredStartViewErr(message || 'Startseite konnte nicht gespeichert werden'),
      }
    );
  }

  async function loescheHistorieEintrag(entryId) {
    if (!(await requestConfirm('Historien-Eintrag wirklich löschen?'))) return;
    await runApi(
      async () => {
        await apiRequest(`/depot/historie/${entryId}`, { method: 'DELETE' });
        setHistorie(items => items.filter(item => item.id !== entryId));
        if (historyEditId === entryId) {
          setHistoryEditId(null);
          setHistoryEditDraft({});
        }
      },
      { defaultValue: null }
    );
  }

  function bearbeiteHistorieEintrag(entry) {
    setHistoryEditId(entry.id);
    setHistoryEditDraft({
      rein_datum: entry.rein_datum || todayInputValue(),
      raus_datum: entry.raus_datum || todayInputValue(),
      menge: entry.menge ?? 0,
      einstandskurs: entry.einstandskurs ?? 0,
      verkaufskurs: entry.verkaufskurs ?? 0,
    });
    setHistoryEditErr('');
  }

  function abbrecheHistorieBearbeitung() {
    setHistoryEditId(null);
    setHistoryEditDraft({});
    setHistoryEditErr('');
  }

  async function speichereHistorieEintrag(entryId) {
    const parseValue = value => Number.parseFloat(String(value ?? '0').replace(',', '.'));
    const payload = {
      rein_datum: historyEditDraft.rein_datum,
      raus_datum: historyEditDraft.raus_datum,
      menge: parseValue(historyEditDraft.menge),
      einstandskurs: parseValue(historyEditDraft.einstandskurs),
      verkaufskurs: parseValue(historyEditDraft.verkaufskurs),
    };
    if (!payload.rein_datum || !payload.raus_datum) {
      setHistoryEditErr('Bitte Kauf- und Verkaufsdatum angeben');
      return;
    }
    if ([payload.menge, payload.einstandskurs, payload.verkaufskurs].some(value => !Number.isFinite(value) || value < 0)) {
      setHistoryEditErr('Menge und Kurse müssen 0 oder größer sein');
      return;
    }
    setHistoryEditSaving(true);
    setHistoryEditErr('');
    await runApi(
      async () => {
        const updated = await apiRequest(`/depot/historie/${entryId}`, {
          method: 'PATCH',
          body: payload,
        });
        setHistorie(items => items.map(item => (item.id === entryId ? updated : item)));
        setHistoryEditId(null);
        setHistoryEditDraft({});
      },
      {
        defaultValue: null,
        onError: message => setHistoryEditErr(message || 'Historien-Eintrag konnte nicht gespeichert werden'),
        fallbackMessage: 'Historien-Eintrag konnte nicht gespeichert werden',
      }
    );
    setHistoryEditSaving(false);
  }

  async function ladeIbTaxYears() {
    const loadedYears = await runApi(
      async () => {
        const data = await apiRequest('/kontoauszug/years');
        return Array.isArray(data?.years) ? data.years : [];
      },
      { defaultValue: null }
    );
    if (Array.isArray(loadedYears)) {
      setIbTaxYears(loadedYears);
      if (loadedYears.length > 0 && !loadedYears.includes(kontoauszugYear)) {
        setKontoauszugYear(loadedYears[loadedYears.length - 1]);
      }
    } else {
      if (ibTaxYears.length === 0) {
        const fallback = Array.from({ length: new Date().getFullYear() - 2019 }, (_, idx) => 2020 + idx);
        setIbTaxYears(fallback);
        if (!fallback.includes(kontoauszugYear)) {
          setKontoauszugYear(fallback[fallback.length - 1]);
        }
      }
    }
  }

  async function analysiereKontoauszug() {
    if (!kontoauszugFile) {
      setKontoauszugErr('Bitte zuerst einen IB-Kontoauszug als CSV auswählen');
      return;
    }
    setKontoauszugErr('');
    setKontoauszugMsg('');
    setKontoauszugLoading(true);
    setKontoauszugJobId('');
    setKontoauszugProgress(0);
    setKontoauszugProgressText('Analyse wird vorbereitet');
    setKontoauszugMeta(null);
    setKontoauszugReferences([]);
    setKontoauszugResults([]);
    setKontoauszugIssues([]);
    await runApi(
      async () => {
        const form = new FormData();
        form.append('file', kontoauszugFile);
        form.append('tax_year', String(kontoauszugYear));
        const data = await apiRequest('/kontoauszug/analyze', {
          method: 'POST',
          body: form,
        });
        setKontoauszugJobId(data?.job_id || '');
        setKontoauszugProgress(typeof data?.progress === 'number' ? data.progress : 0);
        setKontoauszugProgressText(data?.message || 'Analyse wurde gestartet');
        setKontoauszugMsg('Analyse wurde gestartet');
      },
      {
        onError: message => {
          setKontoauszugErr(message || 'Kontoauszug konnte nicht ausgewertet werden');
          setKontoauszugProgress(0);
          setKontoauszugProgressText('');
        },
        fallbackMessage: 'Kontoauszug konnte nicht ausgewertet werden',
      }
    );
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!kontoauszugJobId || !kontoauszugLoading) return undefined;
    let cancelled = false;

    async function poll() {
      try {
        const data = await apiRequest(`/kontoauszug/analyze/${kontoauszugJobId}`, { timeoutMs: 30000 });
        if (cancelled) return;
        setKontoauszugProgress(typeof data?.progress === 'number' ? data.progress : 0);
        setKontoauszugProgressText(data?.message || '');
        if (data?.status === 'done') {
          setKontoauszugMeta(data?.result?.meta || null);
          setKontoauszugReferences(Array.isArray(data?.result?.references) ? data.result.references : []);
          setKontoauszugResults(Array.isArray(data?.result?.results) ? data.result.results : []);
          setKontoauszugIssues(Array.isArray(data?.result?.issues) ? data.result.issues : []);
          setKontoauszugMsg('Kontoauszug wurde ausgewertet');
          setKontoauszugLoading(false);
          return;
        }
        if (data?.status === 'failed') {
          setKontoauszugErr(data?.error || 'Kontoauszug konnte nicht ausgewertet werden');
          setKontoauszugLoading(false);
          return;
        }
        kontoauszugPollTimeoutRef.current = window.setTimeout(poll, 1200);
      } catch (err) {
        if (cancelled) return;
        if (handleSessionError(err)) return;
        setKontoauszugErr(err?.message || 'Analyse-Status konnte nicht geladen werden');
        setKontoauszugLoading(false);
      }
    }

    poll();
    return () => {
      cancelled = true;
      if (kontoauszugPollTimeoutRef.current) {
        window.clearTimeout(kontoauszugPollTimeoutRef.current);
        kontoauszugPollTimeoutRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kontoauszugJobId, kontoauszugLoading, setToken]);

  async function login() {
    setLoginFehler('');
    setLoginHelpMsg('');
    setLoginHelpErr('');
    if (!email.trim()) {
      setLoginFehler('Bitte E-Mail eingeben');
      return;
    }
    if (!password) {
      setLoginFehler('Bitte Passwort eingeben');
      return;
    }
    try {
      const data = await apiRequest('/token', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: `username=${encodeURIComponent(email)}&password=${encodeURIComponent(password)}`,
        auth: false,
        retryOnUnauthorized: false,
      });

      if (data?.requires_2fa) {
        setPreauthToken(data.preauth_token || '');
        setLogin2faCode('');
        setLogin2faMsg(data.message || '2FA-Code wurde per E-Mail gesendet');
        setLogin2faErr('');
        setAuthMode('login2fa');
        return;
      }

      setStoredAuth();
      preferredStartRedirectPendingRef.current = true;
      setLoginFailureCount(0);
      setLoginWaitSeconds(0);
      setToken('__cookie__');
    } catch (err) {
      setLoginFehler(err?.message || 'E-Mail oder Passwort falsch');
      setLoginFailureCount(current => current + 1);
      const waitMatch = String(err?.message || '').match(/(\d+)\s+Sekunden/i);
      setLoginWaitSeconds(waitMatch ? Number(waitMatch[1]) : 0);
    }
  }

  async function requestLoginLink() {
    setLoginHelpErr('');
    setLoginHelpMsg('');
    if (!email.trim()) {
      setLoginHelpErr('Bitte zuerst deine E-Mail-Adresse eingeben');
      return;
    }
    setLoginHelpLoading('login-link');
    try {
      const data = await apiRequest('/request-login-link', {
        method: 'POST',
        auth: false,
        retryOnUnauthorized: false,
        body: { email: email.trim() },
      });
      setLoginHelpMsg(data.message || 'Falls dein Account freigeschaltet ist, wurde ein Login-Link gesendet.');
    } catch (err) {
      setLoginHelpErr(err?.message || 'Login-Link konnte nicht angefordert werden');
    } finally {
      setLoginHelpLoading('');
    }
  }

  async function resendVerificationFromLogin() {
    setLoginHelpErr('');
    setLoginHelpMsg('');
    if (!email.trim()) {
      setLoginHelpErr('Bitte zuerst deine E-Mail-Adresse eingeben');
      return;
    }
    setLoginHelpLoading('verification');
    try {
      const data = await apiRequest('/resend-verification', {
        method: 'POST',
        auth: false,
        retryOnUnauthorized: false,
        body: { email: email.trim() },
      });
      setLoginHelpMsg(data.message || 'Falls eine Bestätigung aussteht, wurde eine neue E-Mail gesendet.');
    } catch (err) {
      setLoginHelpErr(err?.message || 'Bestätigungsmail konnte nicht gesendet werden');
    } finally {
      setLoginHelpLoading('');
    }
  }

  async function confirmLoginLink() {
    setLoginLinkErr('');
    setLoginLinkMsg('');
    if (!loginLinkToken) {
      setLoginLinkErr('Der Login-Link ist unvollständig');
      return;
    }
    setLoginLinkSaving(true);
    try {
      await apiRequest('/token/login-link', {
        method: 'POST',
        auth: false,
        retryOnUnauthorized: false,
        body: { token: loginLinkToken },
      });
      setLoginLinkToken('');
      window.history.replaceState({}, '', '/');
      setStoredAuth();
      preferredStartRedirectPendingRef.current = true;
      setAuthMode('login');
      setToken('__cookie__');
    } catch (err) {
      setLoginLinkErr(err?.message || 'Anmeldung mit Login-Link fehlgeschlagen');
    } finally {
      setLoginLinkSaving(false);
    }
  }

  function leaveLoginLink() {
    window.history.replaceState({}, '', '/');
    setLoginLinkToken('');
    setLoginLinkErr('');
    setLoginLinkMsg('');
    setAuthMode('login');
  }

  async function bestaetigeLogin2FA() {
    setLogin2faErr('');
    setLogin2faMsg('');
    if (!login2faCode.trim()) {
      setLogin2faErr('Bitte 2FA-Code eingeben');
      return;
    }
    try {
      await apiRequest('/token/2fa', {
        method: 'POST',
        auth: false,
        retryOnUnauthorized: false,
        body: {
          preauth_token: preauthToken,
          code: login2faCode.trim(),
        },
      });
      setStoredAuth();
      preferredStartRedirectPendingRef.current = true;
      setToken('__cookie__');
      setPreauthToken('');
      setLogin2faCode('');
    } catch (err) {
      setLogin2faErr(err?.message || '2FA-Code konnte nicht bestätigt werden');
    }
  }

  async function registerAccount() {
    setRegisterErr('');
    setRegisterMsg('');
    if (!registerEmail.trim()) {
      setRegisterErr('Bitte E-Mail-Adresse eingeben');
      return;
    }
    if (!registerPassword) {
      setRegisterErr('Bitte Passwort eingeben');
      return;
    }
    try {
      const attribution = captureRegistrationAttribution();
      const data = await apiRequest('/register', {
        method: 'POST',
        auth: false,
        retryOnUnauthorized: false,
        body: {
          email: registerEmail.trim(),
          password: registerPassword,
          newsletter_opt_in: registerNewsletter,
          newsletter_consent_version: NEWSLETTER_CONSENT_VERSION,
          ...attribution,
        },
      });

      setRegisterMsg(data.message || 'Registrierung gestartet');
      setVerifyEmail(registerEmail.trim());
      setAuthMode('verify');
      setRegisterPassword('');
    } catch (err) {
      setRegisterErr(err?.message || 'Registrierung fehlgeschlagen');
    }
  }

  async function verifyEmailCode() {
    setVerifyErr('');
    setVerifyMsg('');
    if (!verifyEmail.trim()) {
      setVerifyErr('Bitte E-Mail-Adresse eingeben');
      return;
    }
    if (!verifyCode.trim()) {
      setVerifyErr('Bitte Verifizierungscode eingeben');
      return;
    }
    try {
      const data = await apiRequest('/verify-email', {
        method: 'POST',
        auth: false,
        retryOnUnauthorized: false,
        body: {
          email: verifyEmail.trim(),
          code: verifyCode.trim(),
        },
      });
      setPassword('');
      setRegisterPassword('');
      setVerifyCode('');
      setVerifyErr('');
      setVerifyMsg(data.message || 'Verifizierung erfolgreich');
      setAuthMode('login');
      setEmail(verifyEmail.trim());
    } catch (err) {
      setVerifyErr(err?.message || 'Verifizierung fehlgeschlagen');
    }
  }

  async function verifyEmailLink() {
    setVerificationLinkErr('');
    setVerificationLinkMsg('');
    if (!verificationLinkToken) {
      setVerificationLinkErr('Der Bestätigungslink ist unvollständig');
      return;
    }
    setVerificationLinkSaving(true);
    try {
      const data = await apiRequest('/verify-email-link', {
        method: 'POST',
        auth: false,
        retryOnUnauthorized: false,
        body: { token: verificationLinkToken },
      });
      setVerificationLinkToken('');
      window.history.replaceState({}, '', '/email-bestaetigen');
      setVerificationLinkMsg(data.message || 'E-Mail erfolgreich verifiziert. Du kannst dich jetzt einloggen.');
    } catch (err) {
      setVerificationLinkErr(err?.message || 'E-Mail konnte nicht verifiziert werden');
    } finally {
      setVerificationLinkSaving(false);
    }
  }

  function leaveEmailVerificationLink() {
    window.history.replaceState({}, '', '/');
    setVerificationLinkToken('');
    setVerificationLinkErr('');
    setVerificationLinkMsg('');
    setAuthMode('login');
  }

  async function resendVerificationCode() {
    setVerifyErr('');
    setVerifyMsg('');
    if (!verifyEmail.trim()) {
      setVerifyErr('Bitte E-Mail-Adresse eingeben');
      return;
    }
    try {
      const data = await apiRequest('/resend-verification', {
        method: 'POST',
        auth: false,
        retryOnUnauthorized: false,
        body: { email: verifyEmail.trim() },
      });
      setVerifyMsg(data.message || 'Neuer Code wurde gesendet');
    } catch (err) {
      setVerifyErr(err?.message || 'Code konnte nicht gesendet werden');
    }
  }

  async function requestPasswordReset() {
    setResetErr('');
    setResetMsg('');
    if (!resetEmail.trim()) {
      setResetErr('Bitte E-Mail-Adresse eingeben');
      return;
    }
    try {
      const data = await apiRequest('/forgot-password', {
        method: 'POST',
        auth: false,
        retryOnUnauthorized: false,
        body: { email: resetEmail.trim() },
      });
      setResetMsg(data.message || 'Falls die E-Mail existiert, wurde ein Reset-Code gesendet');
      setVerifyEmail(resetEmail.trim());
    } catch (err) {
      setResetErr(err?.message || 'Reset-Code konnte nicht angefordert werden');
    }
  }

  async function confirmPasswordReset() {
    setResetErr('');
    setResetMsg('');
    if (!resetEmail.trim()) {
      setResetErr('Bitte E-Mail-Adresse eingeben');
      return;
    }
    if (!resetCode.trim()) {
      setResetErr('Bitte Reset-Code eingeben');
      return;
    }
    if (!resetPassword) {
      setResetErr('Bitte neues Passwort eingeben');
      return;
    }
    try {
      const data = await apiRequest('/reset-password', {
        method: 'POST',
        auth: false,
        retryOnUnauthorized: false,
        body: {
          email: resetEmail.trim(),
          code: resetCode.trim(),
          new_password: resetPassword,
        },
      });
      setResetMsg(data.message || 'Passwort wurde erfolgreich zurückgesetzt');
      setAuthMode('login');
      setEmail(resetEmail.trim());
      setPassword('');
      setResetCode('');
      setResetPassword('');
    } catch (err) {
      setResetErr(err?.message || 'Passwort konnte nicht zurückgesetzt werden');
    }
  }

  async function accountLoeschen() {
    setDeleteErr('');
    setDeleteMsg('');

    if (!deletePw) {
      setDeleteErr('Bitte aktuelles Passwort eingeben');
      return;
    }

    if (!(await requestConfirm('Account wirklich deaktivieren? Du kannst ihn innerhalb von 30 Tagen mit derselben E-Mail-Adresse wiederherstellen.'))) {
      return;
    }

    setDeleteSaving(true);
    try {
      const data = await apiRequest('/me', {
        method: 'DELETE',
        body: { password: deletePw, keep_newsletter: keepNewsletter && newsletterStatus === 'subscribed' },
      });
      setDeleteMsg(data.message || 'Account wurde gelöscht');
      resetClientSession();
    } catch (err) {
      if (handleSessionError(err)) return;
      setDeleteErr(err?.message || 'Account konnte nicht gelöscht werden');
    } finally {
      setDeleteSaving(false);
    }
  }

  async function logout() {
    try {
      await apiRequest('/token/revoke', {
        method: 'POST',
        body: {},
      });
    } catch {
      // Local session clear should still proceed even if revoke fails.
    }
    resetClientSession();
  }

  async function starteBillingCheckout(productKey) {
    if (!productKey) return;
    setBillingActionLoading(productKey);
    try {
      const data = await apiRequest('/billing/checkout-session', {
        method: 'POST',
        body: { product_key: productKey },
      });
      if (data?.url) {
        window.location.assign(data.url);
        return;
      }
      showToast('Checkout-Link fehlt', 'error');
    } catch (err) {
      if (!handleSessionError(err)) {
        showToast(err?.message || 'Checkout konnte nicht gestartet werden', 'error');
      }
    } finally {
      setBillingActionLoading('');
    }
  }

  async function oeffneBillingPortal() {
    setBillingActionLoading('portal');
    try {
      const data = await apiRequest('/billing/portal-session', {
        method: 'POST',
        body: {},
      });
      if (data?.url) {
        window.location.assign(data.url);
        return;
      }
      showToast('Portal-Link fehlt', 'error');
    } catch (err) {
      if (!handleSessionError(err)) {
        showToast(err?.message || 'Billing-Portal konnte nicht geöffnet werden', 'error');
      }
    } finally {
      setBillingActionLoading('');
    }
  }

  async function akzeptiereRisikohinweis() {
    setRiskAcceptErr('');
    setRiskAcceptSaving(true);
    try {
      await runApi(
        async () => {
          const data = await apiRequest('/me/risk-disclaimer', {
            method: 'PATCH',
            body: { accepted: true },
          });
          setRiskAcceptedAt(data?.risk_disclaimer_accepted_at || '');
          setRiskAcceptedEmail(data?.risk_disclaimer_accepted_email || '');
          setRiskAcceptedVersion(data?.risk_disclaimer_accepted_version || '');
          setRiskCurrentVersion(data?.risk_disclaimer_current_version || RISK_DISCLAIMER_VERSION);
        },
        { onError: setRiskAcceptErr, fallbackMessage: 'Risikohinweis konnte nicht bestätigt werden' }
      );
    } finally {
      setRiskAcceptSaving(false);
    }
  }

  async function speichereHerkunft(skipped = false) {
    setAcquisitionErr('');
    if (!skipped && !acquisitionSource) {
      setAcquisitionErr('Bitte eine Auswahl treffen');
      return;
    }
    setAcquisitionSaving(true);
    try {
      const data = await apiRequest('/me/acquisition-source', {
        method: 'PATCH',
        body: skipped
          ? { skipped: true }
          : {
              source: acquisitionSource,
              detail: acquisitionDetail.trim() || null,
              skipped: false,
            },
      });
      setAcquisitionSource(data?.acquisition_source || acquisitionSource);
      setAcquisitionDetail(data?.acquisition_detail || '');
      setAcquisitionAnsweredAt(data?.acquisition_answered_at || '');
      setAcquisitionSkippedAt(data?.acquisition_skipped_at || '');
    } catch (err) {
      if (!handleSessionError(err)) {
        setAcquisitionErr(err?.message || 'Antwort konnte nicht gespeichert werden');
      }
    } finally {
      setAcquisitionSaving(false);
    }
  }

  async function passwortAendern() {
    setPwMsg('');
    setPwErr('');

    if (!pwCurrent || !pwNew || !pwConfirm) {
      setPwErr('Bitte alle Passwortfelder ausfüllen');
      return;
    }
    if (pwNew !== pwConfirm) {
      setPwErr('Neues Passwort und Wiederholung stimmen nicht überein');
      return;
    }
    if (pwNew.length < 10) {
      setPwErr('Neues Passwort muss mindestens 10 Zeichen haben');
      return;
    }

    setPwSaving(true);
    try {
      await runApi(
        async () => {
          await apiRequest('/me/password', {
            method: 'PATCH',
            body: { current_password: pwCurrent, new_password: pwNew },
          });
          setPwMsg('Passwort erfolgreich geändert');
          setPwCurrent('');
          setPwNew('');
          setPwConfirm('');
        },
        { onError: setPwErr, fallbackMessage: 'Server nicht erreichbar' }
      );
    } finally {
      setPwSaving(false);
    }
  }

  function toggleRslEmailDay(dayId) {
    if (!isAdmin && accountPlanStatus === 'expired') {
      showToast(
        'Zugang ist abgelaufen. Upgrade auf Essential oder Plus nötig.',
        'info',
        { durationMs: 8000, actionLabel: 'hier abschließen', onAction: openAccountPlanOverview },
      );
      return;
    }
    const isEssentialRestricted = !isAdmin && (accountPlan === 'basic' || accountPlan === 'free');
    if (isEssentialRestricted && dayId !== 'mon') {
      showToast(
        'Für Essential ist der Depotstatus per E-Mail nur montags verfügbar. Upgrade auf Plus für weitere Tage.',
        'info',
        { durationMs: 8000, actionLabel: 'hier abschließen', onAction: openAccountPlanOverview },
      );
      return;
    }
    setRslEmailDays(days => (
      days.includes(dayId) ? days.filter(day => day !== dayId) : [...days, dayId]
    ));
  }

  function showPlusUpgradeHint(featureLabel = 'Diese Funktion') {
    showToast(
      `${featureLabel} ist nur mit Plus verfügbar.`,
      'info',
      { durationMs: 8000, actionLabel: 'hier abschließen', onAction: openAccountPlanOverview },
    );
  }

  function handleScreenerTabSelect(nextTabKey) {
    const isEssentialPlan = !isAdmin && (accountPlan === 'basic' || accountPlan === 'free');
    const blockedForEssential = nextTabKey === 'NASDAQ100' || nextTabKey === 'STOXX600';
    if (isEssentialPlan && blockedForEssential) {
      showPlusUpgradeHint('Nasdaq 100 und STOXX 600');
      return;
    }
    setScreenerTab(nextTabKey);
  }

  async function speichereTradeJournalKonto() {
    setTradeJournalErr('');
    setTradeJournalMsg('');
    if (!tradeJournalAccountLabel.trim()) {
      setTradeJournalErr('Bitte eine Kontobezeichnung eingeben');
      return;
    }
    if (!tradeJournalQueryId.trim()) {
      setTradeJournalErr('Bitte die Query-ID fuer den Daily Import eingeben');
      return;
    }
    if (!tradeJournalFlexTokenSet && !tradeJournalFlexToken.trim()) {
      setTradeJournalErr('Bitte einen Flex Token hinterlegen');
      return;
    }

    setTradeJournalSaving(true);
    try {
      const data = await apiRequest('/trade-journal/account', {
        method: 'PATCH',
        body: {
          account_label: tradeJournalAccountLabel,
          query_id: tradeJournalQueryId,
          ...(tradeJournalFlexToken.trim() ? { flex_token: tradeJournalFlexToken.trim() } : {}),
        },
      });
      applyTradeJournalAccountPayload(data);
      setTradeJournalMsg('Trade-Journal-Konfiguration gespeichert');
    } catch (err) {
      if (handleSessionError(err)) return;
      setTradeJournalErr(err?.message || 'Trade-Journal-Konfiguration konnte nicht gespeichert werden');
    } finally {
      setTradeJournalSaving(false);
    }
  }

  async function generiereTradeJournalAgentSecret() {
    setTradeJournalErr('');
    setTradeJournalMsg('');
    setTradeJournalAgentGenerating(true);
    try {
      const data = await apiRequest('/trade-journal/live-agent/generate-secret', {
        method: 'POST',
      });
      setTradeJournalAgentSecretSet(!!data.secret_set);
      setTradeJournalAgentSecretValue(data.secret || '');
      setTradeJournalMsg('Agent-Secret erzeugt. Bitte jetzt sicher im Windows-Agent hinterlegen.');
      await ladeTradeJournalAccount();
    } catch (err) {
      if (handleSessionError(err)) return;
      setTradeJournalErr(err?.message || 'Agent-Secret konnte nicht erzeugt werden');
    } finally {
      setTradeJournalAgentGenerating(false);
    }
  }

  function applyTradeJournalAccountPayload(data) {
    setTradeJournalAccountLabel(data.account_label || '');
    setTradeJournalQueryId(data.query_id || '');
    setTradeJournalFlexToken('');
    setTradeJournalFlexTokenSet(!!data.flex_token_set);
    setTradeJournalAgentSecretSet(!!data.ib_live_agent_secret_set);
    setTradeJournalAgentName(data.ib_live_agent_name || '');
    setTradeJournalAgentVersion(data.ib_live_agent_version || '');
    setTradeJournalAgentStatus(data.ib_live_agent_status || 'not_configured');
    setTradeJournalAgentStatusLabel(data.ib_live_agent_status_label || 'Nicht eingerichtet');
    setTradeJournalAgentStatusDetail(data.ib_live_agent_status_detail || '');
    setTradeJournalAgentStatusColor(data.ib_live_agent_status_color || 'ghost');
    setTradeJournalAgentLastSeenAt(data.ib_live_agent_last_seen_at || '');
    setTradeJournalAgentLastRequestAt(data.ib_live_agent_last_request_at || '');
    setTradeJournalAgentLastQuoteAt(data.ib_live_agent_last_quote_at || '');
    setTradeJournalAgentLastError(data.ib_live_agent_last_error || '');
    setTradeJournalAgentLastErrorAt(data.ib_live_agent_last_error_at || '');
    setTradeJournalAgentTwsConnected(typeof data.ib_live_agent_tws_connected === 'boolean' ? data.ib_live_agent_tws_connected : null);
    setTradeJournalAgentLastTwsConnectedAt(data.ib_live_agent_last_tws_connected_at || '');
    setTradeJournalAgentLiveTwsConnected(typeof data.ib_live_agent_live_tws_connected === 'boolean' ? data.ib_live_agent_live_tws_connected : null);
    setTradeJournalAgentLiveLastTwsConnectedAt(data.ib_live_agent_live_last_tws_connected_at || '');
    setTradeJournalAgentPaperTwsConnected(typeof data.ib_live_agent_paper_tws_connected === 'boolean' ? data.ib_live_agent_paper_tws_connected : null);
    setTradeJournalAgentPaperLastTwsConnectedAt(data.ib_live_agent_paper_last_tws_connected_at || '');
    setTradeJournalAgentLastQuoteDurationMs(Number.isFinite(Number(data.ib_live_agent_last_quote_duration_ms))
      ? Number(data.ib_live_agent_last_quote_duration_ms)
      : null);
    setTradeJournalLastSyncAt(data.last_sync_at || '');
    setTradeJournalLastSyncStatus(data.last_sync_status || '');
    setTradeJournalLastSyncError(data.last_sync_error || '');
    setTradeJournalLastSyncImportedCount(Number(data.last_sync_imported_count || 0));
    setTradeJournalLastSyncSkippedCount(Number(data.last_sync_skipped_count || 0));
    setTradeJournalLastSyncItems(Array.isArray(data.last_sync_items) ? data.last_sync_items : []);
  }

  async function requestTradeJournalAgentRefreshAndWait() {
    const previousQuoteAt = tradeJournalAgentLastQuoteAt || '';
    try {
      await apiRequest('/trade-journal/live-agent/request-refresh', {
        method: 'POST',
        timeoutMs: 15000,
      });
    } catch (err) {
      return;
    }

    const maxWaitMs = 25000;
    const pollIntervalMs = 2000;
    const deadline = Date.now() + maxWaitMs;

    while (Date.now() < deadline) {
      await new Promise(resolve => setTimeout(resolve, pollIntervalMs));
      try {
        const accountData = await apiRequest('/trade-journal/account', { timeoutMs: 15000 });
        applyTradeJournalAccountPayload(accountData);
        const latestQuoteAt = accountData?.ib_live_agent_last_quote_at || '';
        if (latestQuoteAt && (!previousQuoteAt || new Date(latestQuoteAt).getTime() > new Date(previousQuoteAt).getTime())) {
          return;
        }
      } catch (err) {
        return;
      }
    }
  }

  async function starteTradeJournalSync() {
    setTradeJournalSyncError('');
    setTradeJournalSyncLoading(true);
    try {
      const data = await apiRequest('/trade-journal/sync', { method: 'POST' });
      setTradeJournalSyncJobId(data.job_id || '');
      setTradeJournalSyncStatus(data.status || '');
      setTradeJournalSyncProgress(Number.isFinite(data.progress) ? data.progress : 0);
      setTradeJournalSyncMessage(data.message || '');
      setTradeJournalSyncFetchedCount(0);
      setTradeJournalSyncImportedCount(0);
      setTradeJournalSyncSkippedCount(0);
      setTradeJournalSyncImportedItems([]);
    } catch (err) {
      if (handleSessionError(err)) return;
      setTradeJournalSyncLoading(false);
      setTradeJournalSyncError(err?.message || 'Trade-Journal-Sync konnte nicht gestartet werden');
    }
  }

  async function importiereTradeJournalDatei(file) {
    setTradeJournalSyncError('');
    setTradeJournalSyncLoading(true);
    setTradeJournalSyncProgress(5);
    setTradeJournalSyncMessage(`Importiere ${file.name}…`);
    setTradeJournalSyncFetchedCount(0);
    setTradeJournalSyncImportedCount(0);
    setTradeJournalSyncSkippedCount(0);
    setTradeJournalSyncImportedItems([]);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const data = await apiRequest('/trade-journal/import-file', {
        method: 'POST',
        body: formData,
      });
      // The endpoint now returns a job_id immediately; poll status like sync
      setTradeJournalSyncJobId(data.job_id || '');
      setTradeJournalSyncStatus(data.status || 'running');
      setTradeJournalSyncProgress(data.progress || 5);
      setTradeJournalSyncMessage(data.message || `Importiere ${file.name}…`);
    } catch (err) {
      if (handleSessionError(err)) return;
      setTradeJournalSyncLoading(false);
      setTradeJournalSyncStatus('failed');
      setTradeJournalSyncError(err?.message || 'Datei-Import fehlgeschlagen');
    }
  }

  async function loescheTradeJournalEintraege() {
    setTradeJournalSyncError('');
    setTradeJournalSyncLoading(true);
    try {
      const data = await apiRequest('/trade-journal/entries', {
        method: 'DELETE',
      });
      setTradeJournalGroups([]);
      setTradeJournalCashSummary(null);
      setTradeJournalCashTransactions([]);
      setTradeJournalCashTransactionsLoaded(false);
      setTradeJournalStatementSummary(null);
      setTradeJournalSetups([]);
      setTradeJournalSyncJobId('');
      setTradeJournalSyncStatus('done');
      setTradeJournalSyncProgress(100);
      setTradeJournalSyncFetchedCount(0);
      setTradeJournalSyncImportedCount(0);
      setTradeJournalSyncSkippedCount(0);
      setTradeJournalSyncImportedItems([]);
      setTradeJournalSyncMessage(`Journal geleert: ${data.deleted_fills || 0} Einträge entfernt`);
      setTradeJournalGroups([]);
      setTradeJournalOverview(null);
      setTradeJournalCashSummary(null);
      setTradeJournalCashTransactions([]);
      setTradeJournalCashTransactionsLoaded(false);
      await ladeTradeJournalStatementSummary();
      await ladeTradeJournalSetups();
      await ladeTradeJournalAccount();
    } catch (err) {
      if (handleSessionError(err)) return;
      setTradeJournalSyncStatus('failed');
      setTradeJournalSyncError(err?.message || 'Trade-Journal-Einträge konnten nicht gelöscht werden');
    } finally {
      setTradeJournalSyncLoading(false);
    }
  }

  async function speichereBenachrichtigungen() {
    setNotificationErr('');
    setNotificationMsg('');
    setNotificationSaving(true);
    try {
      const data = await apiRequest('/me/notifications', {
        method: 'PATCH',
        body: {
          email_notifications_enabled: emailNotificationsEnabled,
          premium_signal_emails_enabled: premiumSignalEmailsEnabled,
          rsl_email_days: rslEmailDays,
          email_theme: emailTheme,
        },
      });
      setEmailNotificationsEnabled(data.email_notifications_enabled !== false);
      setPremiumSignalEmailsEnabled(data.premium_signal_emails_enabled !== false);
      setRslEmailDays(Array.isArray(data.rsl_email_days) ? data.rsl_email_days : []);
      setEmailTheme(data?.email_theme === 'light' ? 'light' : 'dark');
      setNotificationMsg('Benachrichtigungseinstellungen gespeichert');
    } catch (err) {
      if (handleSessionError(err)) return;
      setNotificationErr(err?.message || 'Benachrichtigungseinstellungen konnten nicht gespeichert werden');
    } finally {
      setNotificationSaving(false);
    }
  }

  async function speichereZweiFaktor() {
    setTwoFactorErr('');
    setTwoFactorMsg('');
    if (!twoFactorCurrentPassword) {
      setTwoFactorErr('Bitte aktuelles Passwort eingeben');
      return;
    }
    setTwoFactorSaving(true);
    try {
      const data = await apiRequest('/me/2fa', {
        method: 'PATCH',
        body: {
          enabled: twoFactorEnabled,
          current_password: twoFactorCurrentPassword,
        },
      });
      setTwoFactorEnabled(!!data.two_factor_enabled);
      setTwoFactorPending(!!data.two_factor_pending);
      setTwoFactorCurrentPassword('');
      setTwoFactorVerificationCode('');
      setTwoFactorMsg(
        data.two_factor_enabled
          ? '2FA aktiviert'
          : data.two_factor_pending
            ? 'Bestätigungscode wurde per E-Mail gesendet'
            : '2FA deaktiviert'
      );
    } catch (err) {
      if (handleSessionError(err)) return;
      setTwoFactorErr(err?.message || '2FA-Einstellung konnte nicht gespeichert werden');
    } finally {
      setTwoFactorSaving(false);
    }
  }

  async function bestaetigeZweiFaktor() {
    setTwoFactorErr('');
    setTwoFactorMsg('');
    if (!twoFactorVerificationCode.trim()) {
      setTwoFactorErr('Bitte 2FA-Bestätigungscode eingeben');
      return;
    }
    setTwoFactorSaving(true);
    try {
      const data = await apiRequest('/me/2fa/confirm', {
        method: 'POST',
        body: { code: twoFactorVerificationCode.trim() },
      });
      setTwoFactorEnabled(!!data.two_factor_enabled);
      setTwoFactorPending(!!data.two_factor_pending);
      setTwoFactorVerificationCode('');
      setTwoFactorMsg('2FA aktiviert');
    } catch (err) {
      if (handleSessionError(err)) return;
      setTwoFactorErr(err?.message || '2FA konnte nicht aktiviert werden');
    } finally {
      setTwoFactorSaving(false);
    }
  }

  async function kaufen(symbol, marktKey, customWatchlistId = null) {
    if (depot.find(p => p.symbol === symbol && p.index === marktKey && (marktKey !== 'CUSTOM' || p.custom_watchlist_id === customWatchlistId))) {
      showToast(`${symbol} ist bereits im Depot`, 'info'); return;
    }
    await runApi(
      async () => {
        await apiRequest('/depot/', {
          method: 'POST',
          body: { symbol, index: marktKey, custom_watchlist_id: customWatchlistId },
        });
        ladeDepot();
      },
      { defaultValue: null }
    );
  }

  async function speichernDepotPosition(id, payload) {
    return runApi(
      async () => {
        await apiRequest(`/depot/${id}`, {
          method: 'PATCH',
          body: payload,
        });
        await ladeDepot();
        return true;
      },
      { rethrow: true }
    );
  }

  async function fuegeDepotTrancheHinzu(id, payload) {
    return runApi(
      async () => {
        await apiRequest(`/depot/${id}/kauf`, {
          method: 'POST',
          body: payload,
        });
        await ladeDepot();
        return true;
      },
      { rethrow: true }
    );
  }

  async function loescheDepotTranche(positionId, trancheId) {
    return runApi(
      async () => {
        await apiRequest(`/depot/${positionId}/kauf/${trancheId}`, {
          method: 'DELETE',
        });
        await ladeDepot();
        return true;
      },
      { rethrow: true }
    );
  }

  function entfernen(position) {
    const selected = typeof position === 'object' && position
      ? position
      : depot.find(item => item.id === position);
    if (!selected) {
      showToast('Position nicht gefunden', 'error');
      return;
    }
    setClosePosition(selected);
    setCloseDate(todayInputValue());
    setClosePrice(Number.isFinite(selected.kurs) ? String(selected.kurs) : '0');
    setCloseSellQuantity(Number.isFinite(selected.menge) && selected.menge > 0 ? String(selected.menge) : '');
    setCloseErr('');
  }

  async function schliesseDepotPosition() {
    if (!closePosition) return;
    const parsedPrice = Number.parseFloat(String(closePrice).replace(',', '.'));
    if (!Number.isFinite(parsedPrice) || parsedPrice < 0) {
      setCloseErr('Bitte einen gültigen Verkaufskurs ab 0 angeben');
      return;
    }
    if (!closeDate) {
      setCloseErr('Bitte ein Verkaufsdatum angeben');
      return;
    }
    const trimmedSellQuantity = String(closeSellQuantity ?? '').trim();
    const parsedSellQuantity = trimmedSellQuantity === ''
      ? null
      : Number.parseFloat(trimmedSellQuantity.replace(',', '.'));
    if (parsedSellQuantity !== null && (!Number.isFinite(parsedSellQuantity) || parsedSellQuantity <= 0)) {
      setCloseErr('Bitte eine gültige Verkaufsmenge größer als 0 angeben oder Feld leer lassen für komplettes Schließen');
      return;
    }
    setCloseSaving(true);
    setCloseErr('');
    await runApi(
      async () => {
        await apiRequest(`/depot/${closePosition.id}`, {
          method: 'DELETE',
          body: {
            raus_datum: closeDate,
            menge: parsedSellQuantity,
            verkaufskurs: parsedPrice,
          },
        });
        setClosePosition(null);
        setClosePrice('');
        setCloseSellQuantity('');
        await ladeDepot();
      },
      {
        defaultValue: null,
        onError: message => setCloseErr(message || 'Titel konnte nicht geschlossen werden'),
        fallbackMessage: 'Titel konnte nicht geschlossen werden',
      }
    );
    setCloseSaving(false);
  }

  async function tickerInZwischenablage(positionen, label) {
    const tickers = positionen.map(p => p.symbol).filter(Boolean);
    if (tickers.length === 0) {
      showToast(`Keine Ticker in ${label} vorhanden`, 'info');
      return;
    }
    const text = tickers.join(', ');
    try {
      await navigator.clipboard.writeText(text);
      showToast(`${label}: ${tickers.length} Ticker in die Zwischenablage kopiert`, 'success');
    } catch (err) {
      showToast(`Zwischenablage nicht verfügbar. Ticker: ${text}`, 'error');
    }
  }

  async function manuellHinzufuegen() {
    const symbol = manuellerTicker.trim().toUpperCase();
    setTickerFehler(''); setTickerErfolg('');
    if (!symbol) { setTickerFehler('Bitte Ticker eingeben'); return; }
    if (depot.find(p => p.symbol === symbol)) {
      setTickerFehler(`${symbol} ist bereits im Depot`); return;
    }
    try {
      await apiRequest('/depot/', {
        method: 'POST',
        body: { symbol, index: 'AUTO' },
      });
      setTickerErfolg(`✓ ${symbol} hinzugefügt`);
      setManuellerTicker('');
      ladeDepot();
      if (tickerErfolgTimerRef.current) {
        window.clearTimeout(tickerErfolgTimerRef.current);
      }
      tickerErfolgTimerRef.current = window.setTimeout(() => {
        setTickerErfolg('');
        tickerErfolgTimerRef.current = null;
      }, 3000);
    } catch (err) {
      if (handleSessionError(err)) {
        return;
      }
      const detail = err instanceof ApiError ? err?.payload?.detail : null;
      if (
        err instanceof ApiError
        && err.status === 409
        && detail
        && typeof detail === 'object'
        && detail.code === 'multiple_index_matches'
        && Array.isArray(detail.markets)
        && detail.markets.length > 1
      ) {
        setManualAddIndexChoice({
          symbol: String(detail.symbol || symbol).toUpperCase(),
          markets: detail.markets.filter(item => typeof item === 'string' && item),
          saving: false,
        });
        return;
      }
      setTickerFehler(err?.message || 'Server nicht erreichbar');
    }
  }

  async function manuellHinzufuegenMitIndex(indexKey) {
    if (!manualAddIndexChoice || manualAddIndexChoice.saving) return;
    const symbol = manualAddIndexChoice.symbol;
    setTickerFehler('');
    setTickerErfolg('');
    setManualAddIndexChoice(current => (current ? { ...current, saving: true } : current));
    try {
      await apiRequest('/depot/', {
        method: 'POST',
        body: { symbol, index: indexKey },
      });
      setManualAddIndexChoice(null);
      setTickerErfolg(`✓ ${symbol} hinzugefügt (${MANUAL_ADD_MARKET_LABELS[indexKey] || indexKey})`);
      setManuellerTicker('');
      ladeDepot();
      if (tickerErfolgTimerRef.current) {
        window.clearTimeout(tickerErfolgTimerRef.current);
      }
      tickerErfolgTimerRef.current = window.setTimeout(() => {
        setTickerErfolg('');
        tickerErfolgTimerRef.current = null;
      }, 3000);
    } catch (err) {
      if (handleSessionError(err)) {
        return;
      }
      setTickerFehler(err?.message || 'Server nicht erreichbar');
      setManualAddIndexChoice(current => (current ? { ...current, saving: false } : current));
    }
  }

  useEffect(() => () => {
    if (tickerErfolgTimerRef.current) {
      window.clearTimeout(tickerErfolgTimerRef.current);
      tickerErfolgTimerRef.current = null;
    }
  }, []);

  async function analyseAll() {
    setLaden(true); setErgebnis(null); setStatusMsg('Lade Daten…');
    await runApi(
      async () => {
        const requestedLimit = (isAdmin || accountPlan === 'premium')
          ? 1000
          : (accountPlan === 'basic' ? 20 : 10);
        const data = await apiRequest(`/api/top10?limit=${requestedLimit}`);
        setErgebnis(data);
        setCachedAt(data.cached_at);
        setCachedAtByMarket(data.cached_at_by_market || {});
        setMarketDataAsOf({
          SP500: data?.market_data_as_of?.SP500 || null,
          HDAX: data?.market_data_as_of?.HDAX || null,
          NASDAQ100: data?.market_data_as_of?.NASDAQ100 || null,
          STOXX600: data?.market_data_as_of?.STOXX600 || null,
        });
        setStatusMsg('');
      },
      { onError: setStatusMsg, fallbackMessage: 'Fehler beim Laden' }
    );
    setLaden(false);
  }

  // ─── LOGIN ────────────────────────────────────────────

  if (!token || authMode === 'verifylink' || authMode === 'loginlink') {
    return (
      <AuthSection
        COLORS={COLORS}
        inputStyle={inputStyle}
        authMode={authMode}
        setAuthMode={setAuthMode}
        email={email}
        setEmail={setEmail}
        password={password}
        setPassword={setPassword}
        loginFehler={loginFehler}
        loginFailureCount={loginFailureCount}
        loginWaitSeconds={loginWaitSeconds}
        loginHelpMsg={loginHelpMsg}
        loginHelpErr={loginHelpErr}
        loginHelpLoading={loginHelpLoading}
        login={login}
        setResetEmail={setResetEmail}
        requestLoginLink={requestLoginLink}
        resendVerificationFromLogin={resendVerificationFromLogin}
        loginLinkToken={loginLinkToken}
        loginLinkMsg={loginLinkMsg}
        loginLinkErr={loginLinkErr}
        loginLinkSaving={loginLinkSaving}
        confirmLoginLink={confirmLoginLink}
        leaveLoginLink={leaveLoginLink}
        login2faCode={login2faCode}
        setLogin2faCode={setLogin2faCode}
        login2faErr={login2faErr}
        login2faMsg={login2faMsg}
        bestaetigeLogin2FA={bestaetigeLogin2FA}
        registerEmail={registerEmail}
        setRegisterEmail={setRegisterEmail}
        registerPassword={registerPassword}
        registerNewsletter={registerNewsletter}
        setRegisterNewsletter={setRegisterNewsletter}
        setRegisterPassword={setRegisterPassword}
        registerErr={registerErr}
        registerMsg={registerMsg}
        registerAccount={registerAccount}
        verifyEmail={verifyEmail}
        setVerifyEmail={setVerifyEmail}
        verifyCode={verifyCode}
        setVerifyCode={setVerifyCode}
        verifyErr={verifyErr}
        verifyMsg={verifyMsg}
        verifyEmailCode={verifyEmailCode}
        resendVerificationCode={resendVerificationCode}
        verificationLinkToken={verificationLinkToken}
        verificationLinkMsg={verificationLinkMsg}
        verificationLinkErr={verificationLinkErr}
        verificationLinkSaving={verificationLinkSaving}
        verifyEmailLink={verifyEmailLink}
        leaveEmailVerificationLink={leaveEmailVerificationLink}
        resetEmail={resetEmail}
        setResetEmailValue={setResetEmail}
        resetCode={resetCode}
        setResetCode={setResetCode}
        resetPassword={resetPassword}
        setResetPassword={setResetPassword}
        resetErr={resetErr}
        resetMsg={resetMsg}
        requestPasswordReset={requestPasswordReset}
        confirmPasswordReset={confirmPasswordReset}
        footer={<AppFooter COLORS={COLORS} />}
      />
    );
  }

  if (!modulesLoaded) {
    return (
      <div style={{
        minHeight: '100vh',
        background: themeMode === 'light'
          ? `radial-gradient(1000px 480px at 15% -5%, ${COLORS.bgSoft} 0%, ${COLORS.bg} 52%, #d8e7ee 100%)`
          : `radial-gradient(1000px 480px at 15% -5%, ${COLORS.bgSoft} 0%, ${COLORS.bg} 45%, #071015 100%)`,
        color: COLORS.text,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: "'Manrope', 'Sora', 'Nunito Sans', 'Segoe UI', sans-serif",
      }}>
        <Card style={{ width: '100%', maxWidth: '420px', textAlign: 'center' }}>
          Lade Benutzerrechte...
        </Card>
      </div>
    );
  }

  // ─── MAIN ─────────────────────────────────────────────

  const sp500  = ergebnis?.SP500  || [];
  const hdax   = ergebnis?.HDAX   || [];
  const nasdaq100 = ergebnis?.NASDAQ100 || [];
  const stoxx600 = ergebnis?.STOXX600 || [];
  const screenerTabs = [
    { key: 'SP500', label: '🇺🇸 S&P 500', rows: sp500, currency: '$' },
    { key: 'HDAX', label: '🇩🇪 HDAX', rows: hdax, currency: '€' },
    { key: 'NASDAQ100', label: '🇺🇸 Nasdaq 100', rows: nasdaq100, currency: '$' },
    { key: 'STOXX600', label: '🇪🇺 STOXX 600', rows: stoxx600, currency: '€' },
  ];
  const activeScreenerTab = screenerTabs.find(tab => tab.key === screenerTab) || screenerTabs[0];
  const getDepotAnalysisValue = (item, key) => {
    const analysisKey = { analysis_exit_pl: 'exitPl', analysis_unsafe_gain: 'unsafeGain', analysis_distance_pct: 'distancePercent' }[key];
    return getPositionAnalysis(item)[analysisKey] ?? null;
  };
  const sortDepotSection = items => sortBySpec(items, depotSort, (item, key) => {
    const { signal } = getPositionSignalInfo(item);
    if (key === 'rang') return Number.isFinite(item?.rang) ? item.rang : null;
    if (key === 'ticker') return item?.symbol;
    if (key === 'name') return item?.name;
    if (key === 'kaufdatum') return item?.rein_datum;
    if (key === 'tage') return Number.isFinite(item?.tage) ? item.tage : null;
    if (key === 'kurs') return Number.isFinite(item?.kurs) ? item.kurs : null;
    if (key === 'rsl_titel') return Number.isFinite(item?.rsl_titel) ? item.rsl_titel : null;
    if (key === 'signal') return signal;
    if (key === 'cost_basis') return getPositionCostBasis(item);
    if (key === 'market_value') return getPositionMarketValue(item);
    if (key === 'pl') return getPositionProfitLoss(item);
    if (key === 'pl_percent') return getPositionProfitLossPercent(item);
    if (key === 'analysis_exit_pl' || key === 'analysis_unsafe_gain' || key === 'analysis_distance_pct') {
      return getDepotAnalysisValue(item, key);
    }
    return item?.[key];
  });
  const depotSP500 = sortDepotSection(depot.filter(p => p.index === 'SP500'));
  const depotHDAX  = sortDepotSection(depot.filter(p => p.index === 'HDAX'));
  const depotNASDAQ100 = sortDepotSection(depot.filter(p => p.index === 'NASDAQ100'));
  const depotSTOXX600 = sortDepotSection(depot.filter(p => p.index === 'STOXX600'));
  const depotCustom = sortDepotSection(depot.filter(p => p.index === 'CUSTOM'));
  const depotPerformanceRows = (() => {
    const groups = new Map();
    const ensureGroup = (symbol, index, customWatchlistId = null, customWatchlistName = '', customCurrency = '') => {
      const cleanSymbol = String(symbol || '').trim().toUpperCase();
      const cleanIndex = String(index || '').trim();
      const sectionKey = cleanIndex === 'CUSTOM' && customWatchlistId ? `CUSTOM:${customWatchlistId}` : cleanIndex;
      const customWatchlist = cleanIndex === 'CUSTOM'
        ? customRslWatchlists.find(row => row.id === customWatchlistId)
        : null;
      const key = `${sectionKey}:${cleanSymbol}`;
      if (!groups.has(key)) {
        groups.set(key, {
          key,
          symbol: cleanSymbol,
          index: sectionKey,
          sectionLabel: cleanIndex === 'CUSTOM' ? (customWatchlistName || 'Eigene Watchlist') : '',
          customWatchlistId,
          currency: cleanIndex === 'CUSTOM'
            ? (customWatchlist?.currency || customCurrency || '')
            : getPositionCurrencyLabel(cleanIndex),
          realized: 0,
          realizedCount: 0,
          openPl: 0,
          openCount: 0,
          missingValuations: 0,
          openQuantity: 0,
          total: 0,
          historyEntries: [],
        });
      }
      return groups.get(key);
    };

    depot.forEach(position => {
      const group = ensureGroup(
        position.symbol,
        position.index,
        position.custom_watchlist_id,
        position.custom_watchlist_name,
        position.currency,
      );
      const menge = getFiniteNumber(position.menge);
      const openPl = getPositionProfitLoss(position);
      if (openPl === null) group.missingValuations += 1;
      else group.openPl += openPl;
      group.openCount += 1;
      group.openQuantity += Number.isFinite(menge) ? menge : 0;
    });

    historie.forEach(entry => {
      const group = ensureGroup(
        entry.symbol,
        entry.index,
        entry.custom_watchlist_id,
        entry.custom_watchlist_name,
        entry.currency,
      );
      const realized = Number.isFinite(entry.realisierter_pl)
        ? Number(entry.realisierter_pl)
        : 0;
      group.realized += realized;
      group.realizedCount += 1;
      group.historyEntries.push(entry);
    });

    return Array.from(groups.values())
      .filter(row => row.symbol)
      .map(row => ({
        ...row,
        openPl: row.missingValuations ? null : row.openPl,
        total: row.missingValuations ? null : row.realized + row.openPl,
      }))
      .sort((a, b) => a.symbol.localeCompare(b.symbol) || a.index.localeCompare(b.index));
  })();
  const toggleDepotSort = key => setDepotSort(current => ({
    key,
    direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc',
  }));
  const closedSignalIds = new Set(
    signalsEntries
      .filter(entry => entry.kind === 'close' && entry.related_signal_id)
      .map(entry => entry.related_signal_id)
  );
  const signalMap = Object.fromEntries(signalsEntries.map(entry => [entry.id, entry]));
  const signalFilterQuery = signalFilter.trim().toLowerCase();
  const signalMatchesFilter = entry => {
    if (!signalFilterQuery) return true;
    const bezugText = entry.related_signal_id && signalMap[entry.related_signal_id]
      ? signalMap[entry.related_signal_id].text
      : '';
    return [entry.text, bezugText]
      .filter(Boolean)
      .some(value => value.toLowerCase().includes(signalFilterQuery));
  };
  const openSignals = signalsEntries.filter(
    entry => entry.kind === 'open' && signalMatchesFilter(entry)
  );
  const offeneSignals = openSignals.filter(entry => !closedSignalIds.has(entry.id));
  const geschlosseneOpenSignals = openSignals.filter(entry => closedSignalIds.has(entry.id));
  const geschlosseneSignals = signalsEntries.filter(
    entry => entry.kind === 'close' && signalMatchesFilter(entry)
  );
  const sichtbareOpenSignals = openSignals.slice(0, signalOpenLimit);
  const sichtbareGeschlosseneSignals = geschlosseneSignals.slice(0, signalCloseLimit);
  const isPlusPlan = accountPlan === 'premium';
  const isEssentialPlan = accountPlan === 'basic';
  const canUsePremiumSignalEmails = isPlusPlan;
  const canUseMarketSignalEmailAlerts = isAdmin || isPlusPlan;
  const canUseRslNotifications = isAdmin || accountPlan === 'free' || accountPlan === 'basic' || accountPlan === 'premium';
  const canExportJournalCsv = isAdmin || isPlusPlan;
  const canExportBacktestCsv = isAdmin || isPlusPlan;
  const canExportIndexInvestCsv = isAdmin || isPlusPlan;
  const canLoadMoreTopSignals = isAdmin || isPlusPlan || isEssentialPlan;
  const canAccessExtendedBacktestMarkets = isAdmin || isPlusPlan;
  const canAccessIndexInvest = isAdmin || moduleAccess.index_invest;
  const activeRiskDisclaimerVersion = riskCurrentVersion || RISK_DISCLAIMER_VERSION;
  const hasAcceptedCurrentRiskDisclaimer = Boolean(
    riskAcceptedAt && riskAcceptedVersion && riskAcceptedVersion === activeRiskDisclaimerVersion
  );
  const acquisitionSkippedTimestamp = acquisitionSkippedAt ? Date.parse(acquisitionSkippedAt) : NaN;
  const acquisitionReminderDue = !Number.isFinite(acquisitionSkippedTimestamp)
    || Date.now() - acquisitionSkippedTimestamp >= ACQUISITION_REMINDER_DAYS * 24 * 60 * 60 * 1000;
  const shouldShowAcquisitionPrompt = Boolean(
    hasAcceptedCurrentRiskDisclaimer && !acquisitionAnsweredAt && acquisitionReminderDue
  );
  const showAcquisitionDetail = ['recommendation', 'partner', 'other'].includes(acquisitionSource);
  const riskAcceptanceText = riskAcceptedAt
    ? `Dieser Risikohinweis / Haftungsausschluss wurde am ${formatDateTime(riskAcceptedAt)} vom Anwender ${riskAcceptedEmail || email} akzeptiert. Version: ${riskAcceptedVersion || 'ohne Version'}.`
    : '';
  const canAccessCoaching = isAdmin || moduleAccess.coaching;
  const navViews = [
    ...(moduleAccess.start ? ['start'] : []),
    ...(moduleAccess.depot ? ['depot'] : []),
    ...(moduleAccess.depot ? ['rsl_journal'] : []),
    ...(moduleAccess.screener ? ['screener'] : []),
    ...(moduleAccess.rsl_backtest ? ['rsl_backtest'] : []),
    ...(moduleAccess.trade_journal ? ['trade_journal'] : []),
    ...(moduleAccess.kontoauszug ? ['kontoauszug'] : []),
    ...(canAccessCoaching ? ['coaching'] : []),
    ...(moduleAccess.academy ? ['academy'] : []),
    ...(canAccessIndexInvest ? ['index_invest'] : []),
    ...(moduleAccess.signals ? ['signals'] : []),
    ...(moduleAccess.support ? ['support'] : []),
    ...(moduleAccess.konto ? ['konto'] : []),
    ...(isAdmin ? ['admin'] : []),
  ];
  const primaryNavOrder = ['start', 'coaching', 'screener', 'depot', 'rsl_journal', 'rsl_backtest', 'index_invest', 'academy'];
  const moreNavOrder = ['trade_journal', 'kontoauszug', 'signals', 'konto', 'support', 'admin'];
  const primaryNavViews = primaryNavOrder.filter(navView => navViews.includes(navView));
  const moreNavViews = [
    ...moreNavOrder.filter(navView => navViews.includes(navView) && !primaryNavOrder.includes(navView)),
    ...navViews.filter(navView => !primaryNavOrder.includes(navView) && !moreNavOrder.includes(navView)),
  ];
  const navIconByView = {
    start: '🏠',
    depot: '💼',
    rsl_journal: '📘',
    screener: '🔍',
    rsl_backtest: '📊',
    trade_journal: '📝',
    kontoauszug: '📄',
    coaching: '🎯',
    academy: '⚙️',
    index_invest: '🚦',
    signals: '📡',
    support: '💬',
    konto: '👤',
    admin: '👥',
  };
  const navLabel = navView => `${navIconByView[navView] || '•'} ${VIEW_LABELS[navView] || navView}`;
  const socialLinks = [
    {
      label: 'Instagram',
      href: 'https://www.instagram.com/einfachtrading.de/',
      icon: 'instagram',
      accent: '#e1306c',
    },
    {
      label: 'YouTube',
      href: 'https://youtube.com/@Einfach-Trading',
      icon: 'youtube',
      accent: '#ff0033',
    },
    {
      label: 'Telegram',
      href: 'https://t.me/einfach_trading_de',
      icon: 'telegram',
      accent: '#229ed9',
    },
  ];
  const renderSocialIcon = link => {
    const commonProps = {
      width: 17,
      height: 17,
      viewBox: '0 0 24 24',
      fill: 'none',
      xmlns: 'http://www.w3.org/2000/svg',
      'aria-hidden': 'true',
      focusable: 'false',
      style: { display: 'block' },
    };
    if (link.icon === 'instagram') {
      return (
        <svg {...commonProps}>
          <rect x="4" y="4" width="16" height="16" rx="5" stroke="currentColor" strokeWidth="2" />
          <circle cx="12" cy="12" r="3.4" stroke="currentColor" strokeWidth="2" />
          <circle cx="17" cy="7" r="1.2" fill="currentColor" />
        </svg>
      );
    }
    if (link.icon === 'telegram') {
      return (
        <svg {...commonProps} viewBox="0 0 24 24">
          <path
            d="M20.6 4.5 3.8 11c-1.1.4-1.1 1.1-.2 1.4l4.3 1.3 1.7 5.1c.2.6.4.8.8.8.4 0 .6-.2.9-.5l2.1-2 4.4 3.2c.8.4 1.3.2 1.5-.8l2.7-12.9c.3-1.2-.4-1.7-1.4-1.3Z"
            fill="currentColor"
          />
          <path d="m8.1 13.5 9.8-6.2-7.7 7.4-.3 3.3-1.8-4.5Z" fill={themeMode === 'light' ? '#f6fbfd' : '#16242c'} opacity="0.95" />
        </svg>
      );
    }
    return (
      <svg {...commonProps} viewBox="0 0 24 24">
        <rect x="3" y="6" width="18" height="12" rx="3.5" fill="currentColor" />
        <path d="M10.4 9.2 15.3 12l-4.9 2.8V9.2Z" fill={themeMode === 'light' ? '#f6fbfd' : '#16242c'} />
      </svg>
    );
  };
  const isMoreActive = moreNavViews.includes(view);
  const preferredStartViewValue = navViews.includes(preferredStartView) ? preferredStartView : (navViews[0] || 'start');
  const closeSellQuantityValue = Number.parseFloat(String(closeSellQuantity).replace(',', '.'));
  const closeEntryPrice = Number(closePosition?.einstandskurs);
  const closeSalePrice = Number.parseFloat(String(closePrice).replace(',', '.'));
  const closeCostBasis = Number.isFinite(closeSellQuantityValue) && Number.isFinite(closeEntryPrice)
    ? closeSellQuantityValue * closeEntryPrice
    : null;
  const closeSaleValue = Number.isFinite(closeSellQuantityValue) && Number.isFinite(closeSalePrice)
    ? closeSellQuantityValue * closeSalePrice
    : null;
  const closeRealizedPl = closeCostBasis !== null && closeSaleValue !== null
    ? closeSaleValue - closeCostBasis
    : null;
  const closeCurrency = closePosition ? getPositionCurrencyLabel(closePosition.index) : '';
  const closeDataIncomplete = closePosition && (
    closePosition.menge === null ||
    closePosition.menge === undefined ||
    closePosition.einstandskurs === null ||
    closePosition.einstandskurs === undefined ||
    Number(closePosition.menge) <= 0 ||
    Number(closePosition.einstandskurs) <= 0
  );

  return (
    <div style={{
      minHeight: '100vh',
      background: themeMode === 'light'
        ? `radial-gradient(1000px 480px at 15% -5%, ${COLORS.bgSoft} 0%, ${COLORS.bg} 52%, #d8e7ee 100%)`
        : `radial-gradient(1000px 480px at 15% -5%, ${COLORS.bgSoft} 0%, ${COLORS.bg} 45%, #071015 100%)`,
      color: COLORS.text,
      fontFamily: "'Manrope', 'Sora', 'Nunito Sans', 'Segoe UI', sans-serif",
    }}>
      {!hasAcceptedCurrentRiskDisclaimer && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 1000,
          background: 'rgba(7,16,21,0.88)', display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '24px'
        }}>
          <Card style={{ maxWidth: '860px', width: '100%', border: `1px solid ${COLORS.yellow}55` }}>
            <h2 style={{ margin: '0 0 14px', fontSize: '22px' }}>Risikohinweis / Haftungsausschluss</h2>
            <div style={{ color: COLORS.muted, fontSize: '13px', marginBottom: '12px' }}>Version {activeRiskDisclaimerVersion}</div>
            <div style={{ color: COLORS.muted, fontSize: '14px', lineHeight: 1.75, maxHeight: '60vh', overflow: 'auto', paddingRight: '8px' }}>
              {RISK_DISCLAIMER_SECTIONS.map(section => (
                <section key={section.title} style={{ marginBottom: '14px' }}>
                  <h3 style={{ margin: '0 0 6px', color: COLORS.text, fontSize: '14px' }}>{section.title}</h3>
                  <p style={{ margin: 0 }}>{section.text}</p>
                </section>
              ))}
            </div>
            {riskAcceptErr && <div style={{ color: COLORS.red, fontSize: '13px', marginTop: '14px' }}>⚠ {riskAcceptErr}</div>}
            <div style={{ marginTop: '18px', display: 'flex', justifyContent: 'flex-end', gap: '10px', flexWrap: 'wrap' }}>
              <Btn onClick={logout} color="ghost" disabled={riskAcceptSaving}>
                Ablehnen und Logout
              </Btn>
              <Btn onClick={akzeptiereRisikohinweis} color="green">
                {riskAcceptSaving ? 'Speichere...' : 'Akzeptieren'}
              </Btn>
            </div>
          </Card>
        </div>
      )}
      {shouldShowAcquisitionPrompt && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 999,
          background: 'rgba(7,16,21,0.88)', display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '24px'
        }}>
          <Card style={{ maxWidth: '560px', width: '100%', border: `1px solid ${COLORS.cyan}55` }}>
            <h2 style={{ margin: '0 0 8px', fontSize: '21px' }}>Wie bist du auf Einfach-Trading aufmerksam geworden?</h2>
            <p style={{ margin: '0 0 18px', color: COLORS.muted, fontSize: '14px', lineHeight: 1.6 }}>
              Deine Antwort hilft uns dabei, unsere Inhalte und Angebote gezielter weiterzuentwickeln.
            </p>
            <label style={{ fontSize: '12px', color: COLORS.muted, display: 'block', marginBottom: '6px' }}>
              Quelle
            </label>
            <select
              value={acquisitionSource}
              onChange={event => {
                setAcquisitionSource(event.target.value);
                setAcquisitionErr('');
                if (!['recommendation', 'partner', 'other'].includes(event.target.value)) {
                  setAcquisitionDetail('');
                }
              }}
              style={{ ...inputStyle, marginBottom: showAcquisitionDetail ? '12px' : '4px' }}
            >
              <option value="">Bitte auswählen</option>
              {ACQUISITION_SOURCE_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
            {showAcquisitionDetail && (
              <>
                <label style={{ fontSize: '12px', color: COLORS.muted, display: 'block', marginBottom: '6px' }}>
                  Von wem oder wo genau? (optional)
                </label>
                <input
                  style={inputStyle}
                  type="text"
                  maxLength={200}
                  placeholder="Kurze Angabe"
                  value={acquisitionDetail}
                  onChange={event => setAcquisitionDetail(event.target.value)}
                />
              </>
            )}
            {acquisitionErr && <div style={{ color: COLORS.red, fontSize: '13px', marginTop: '12px' }}>⚠ {acquisitionErr}</div>}
            <div style={{ marginTop: '18px', display: 'flex', justifyContent: 'flex-end', gap: '10px', flexWrap: 'wrap' }}>
              <Btn onClick={() => speichereHerkunft(true)} color="ghost" disabled={acquisitionSaving}>
                Später beantworten
              </Btn>
              <Btn onClick={() => speichereHerkunft(false)} disabled={acquisitionSaving}>
                {acquisitionSaving ? 'Speichere...' : 'Antwort speichern'}
              </Btn>
            </div>
          </Card>
        </div>
      )}
      {/* NAVBAR */}
      {hasAcceptedCurrentRiskDisclaimer && !shouldShowAcquisitionPrompt && (
        <NewsletterPrompt key={email} COLORS={COLORS} onStatusChange={setNewsletterStatus} />
      )}
      <div style={{
        borderBottom: `1px solid ${COLORS.border}`, padding: '0 32px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', minHeight: '58px',
        paddingTop: '8px', paddingBottom: '8px', flexWrap: 'wrap', gap: '10px 16px',
        background: themeMode === 'light' ? '#f4fbfdcc' : '#0e1e26cc',
        backdropFilter: 'blur(10px)', position: 'sticky', top: 0, zIndex: 100,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', maxWidth: '100%', minWidth: 0 }}>
          <img
            src={themeMode === 'light' ? '/ET_App_Logo_Schild_green.png' : '/ET_App_Logo_Schild_white.png'}
            alt="Einfach-Trading.de"
            style={{ height: '64px', width: 'auto', display: 'block' }}
          />
          <span style={{ fontWeight: '700', fontSize: '16px' }}>Einfach-Trading.de</span>
          <Badge color="yellow">Version 1.1.0</Badge>
          <Badge color={getPlanBadgeColor(accountPlan)}>
            {formatPlanLabel(accountPlan)}
          </Badge>
          {accountPlanStatus === 'trial' && <Badge color="cyan">Trial</Badge>}
          {accountPlanStatus === 'expired' && <Badge color="red">Abgelaufen</Badge>}
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', marginLeft: '8px' }}>
            {socialLinks.map(link => (
              <a
                key={link.label}
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                title={link.label}
                aria-label={link.label}
                style={{
                  width: '30px',
                  height: '30px',
                  borderRadius: '9px',
                  border: `1px solid ${themeMode === 'light' ? '#8ec9d9' : COLORS.border}`,
                  background: themeMode === 'light' ? '#f6fbfd' : '#16242c',
                  color: link.accent,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textDecoration: 'none',
                  boxShadow: themeMode === 'light'
                    ? '0 5px 12px rgba(51, 123, 148, 0.1)'
                    : '0 5px 12px rgba(0, 0, 0, 0.2)',
                  fontWeight: 900,
                  lineHeight: 1,
                }}
              >
                {renderSocialIcon(link)}
              </a>
            ))}
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end', marginLeft: 'auto' }}>
          {primaryNavViews.map(v => (
            <a
              key={v}
              href={getPathForView(v)}
              style={{
                background: view === v ? COLORS.accent : 'transparent',
                color: view === v ? '#fff' : COLORS.muted,
                border: 'none',
                borderRadius: '8px',
                padding: '7px 16px',
                fontSize: '13px',
                fontWeight: '600',
                cursor: 'pointer',
                textDecoration: 'none',
                display: 'inline-block',
              }}
            >
              {navLabel(v)}
            </a>
          ))}
          {(moreNavViews.length > 0 || token) && (
            <div ref={moreMenuRef} style={{ position: 'relative' }}>
              <button
                onClick={() => setMoreMenuOpen(current => !current)}
                style={{
                  background: isMoreActive || moreMenuOpen ? COLORS.accent : 'transparent',
                  color: isMoreActive || moreMenuOpen ? '#fff' : COLORS.muted,
                  border: 'none',
                  borderRadius: '8px',
                  padding: '7px 16px',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer',
                }}
              >
                ⋯ Mehr
              </button>
              {moreMenuOpen && (
                <div
                  style={{
                    position: 'absolute',
                    top: '42px',
                    right: 0,
                    minWidth: '220px',
                    background: themeMode === 'light' ? '#f8fdff' : '#13232c',
                    border: `1px solid ${COLORS.border}`,
                    borderRadius: '10px',
                    padding: '6px',
                    boxShadow: themeMode === 'light'
                      ? '0 10px 26px rgba(17, 94, 123, 0.16)'
                      : '0 12px 28px rgba(0, 0, 0, 0.35)',
                    zIndex: 140,
                  }}
                >
                  {moreNavViews.map(v => (
                    <a
                      key={v}
                      href={getPathForView(v)}
                      onClick={() => setMoreMenuOpen(false)}
                      style={{
                        width: '100%',
                        textAlign: 'left',
                        background: view === v ? COLORS.accent : 'transparent',
                        color: view === v ? '#fff' : COLORS.text,
                        border: 'none',
                        borderRadius: '8px',
                        padding: '8px 10px',
                        fontSize: '13px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        textDecoration: 'none',
                        display: 'block',
                      }}
                    >
                      {navLabel(v)}
                    </a>
                  ))}
                  <button
                    onClick={logout}
                    style={{
                      width: '100%',
                      textAlign: 'left',
                      background: 'transparent',
                      color: COLORS.text,
                      border: 'none',
                      borderTop: `1px solid ${COLORS.border}`,
                      borderRadius: '0 0 8px 8px',
                      marginTop: '4px',
                      padding: '10px 10px 8px',
                      fontSize: '13px',
                      fontWeight: '600',
                      cursor: 'pointer',
                    }}
                  >
                    🚪 Logout
                  </button>
                </div>
              )}
            </div>
          )}
          <button
            onClick={toggleThemeMode}
            title={themeMode === 'dark' ? 'Helle Darstellung aktivieren' : 'Dunkle Darstellung aktivieren'}
            aria-label={themeMode === 'dark' ? 'Helle Darstellung aktivieren' : 'Dunkle Darstellung aktivieren'}
            style={{
              width: '34px',
              height: '34px',
              borderRadius: '10px',
              border: `1px solid ${themeMode === 'light' ? '#8ec9d9' : COLORS.border}`,
              background: themeMode === 'light' ? '#f6fbfd' : '#16242c',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              boxShadow: themeMode === 'light'
                ? '0 6px 14px rgba(51, 123, 148, 0.12)'
                : '0 6px 14px rgba(0, 0, 0, 0.22)',
              fontSize: '16px',
            }}
          >
            <span
              aria-hidden="true"
              style={{
                lineHeight: 1,
                transform: themeMode === 'light' ? 'translateY(-1px)' : 'none',
              }}
            >
              {themeMode === 'light' ? '🌙' : '☀️'}
            </span>
          </button>
        </div>
      </div>
      {appUpdateAvailable && (
        <div style={{
          borderBottom: `1px solid ${COLORS.border}`,
          background: themeMode === 'light' ? '#e7f7fb' : '#102b34',
          padding: '10px 20px',
          textAlign: 'center',
          position: 'sticky',
          top: '82px',
          zIndex: 95,
        }}>
          <button
            onClick={() => window.location.reload()}
            style={{
              border: `1px solid ${COLORS.cyan}`,
              background: 'transparent',
              color: COLORS.cyan,
              borderRadius: '999px',
              padding: '7px 14px',
              fontSize: '13px',
              fontWeight: 800,
              cursor: 'pointer',
            }}
          >
            Update verfügbar. Seite neu laden
          </button>
        </div>
      )}

      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '24px 20px 30px' }}>

        {/* ── START ── */}
        {view === 'start' && (
          <div>
            {accountPlanStatus === 'expired' && (
              <Card style={{ marginBottom: '24px', border: `1px solid ${COLORS.yellow}55`, background: 'linear-gradient(135deg, rgba(245,158,11,0.14), rgba(18,35,44,1))' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                  <div>
                    <div style={{ color: COLORS.yellow, fontSize: '12px', fontWeight: '700', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '6px' }}>
                      Demo abgelaufen
                    </div>
                    <div style={{ fontSize: '18px', fontWeight: '700', marginBottom: '6px' }}>
                      Dein 30-Tage-Testzugang ist beendet
                    </div>
                    <div style={{ color: COLORS.muted, fontSize: '14px', lineHeight: 1.7 }}>
                      Upgrade auf Essential oder Plus, um wieder Zugriff zu erhalten.
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <Btn onClick={() => setView('konto')} color="accent" size="sm">Jetzt abonnieren</Btn>
                  </div>
                </div>
              </Card>
            )}
            <Card style={{ marginBottom: '24px', overflow: 'hidden' }}>
              <div style={{
                padding: '28px',
                borderRadius: '16px',
                background: 'linear-gradient(135deg, rgba(34,197,94,0.14) 0%, rgba(6,182,212,0.10) 55%, rgba(14,30,38,0.08) 100%)',
                border: `1px solid ${COLORS.border}`,
              }}>
                <div style={{ fontSize: '12px', letterSpacing: '0.12em', textTransform: 'uppercase', color: COLORS.muted, marginBottom: '10px' }}>
                  Willkommen
                </div>
                <h1 style={{ margin: '0 0 12px', fontSize: '32px', lineHeight: 1.15 }}>
                  Einkommen mit regelbasierten Börsenstrategien
                </h1>
                <div style={{ maxWidth: '860px', color: COLORS.muted, fontSize: '15px', lineHeight: 1.75 }}>
                  <p style={{ margin: '0 0 14px' }}>
                    In der Einfach-Trading App findest Du eine komfortable Umsetzung der „RSL-Strategie“, um regelbasiert von trendstarken Momentum-Aktien profitieren zu können:
                  </p>
                  <ul style={{ margin: '0 0 14px 0', padding: 0, listStyleType: 'none', color: COLORS.text, fontSize: '15px', lineHeight: 1.35 }}>
                    <li style={{ marginBottom: '6px', fontSize: '15px' }}>🔍 Scanner: Finde neue Aktien mit Top-Signalen nach RSL</li>
                    <li style={{ marginBottom: '6px', fontSize: '15px' }}>💼 Depot: Verwalte Aktien, in die Du investiert bist</li>
                    <li style={{ marginBottom: '6px', fontSize: '15px' }}>📘 Journal: Werte Deinen Erfolg über die Zeit aus</li>
                    <li style={{ marginBottom: '6px', fontSize: '15px' }}>📊 Backtest: Teste, wie Strategien in der Vergangenheit performt haben</li>
                  </ul>
                  <p style={{ margin: '0 0 12px' }}>
                    Du entscheidest <strong>selber</strong>, wie häufig Du dein Depot auf Anpassungsbedarf prüfen möchtest. Stell unter Mehr {'>'} Profil ein, wie oft Du per E-Mail zum Stand Deines Depots informiert werden möchtest. Nutze dieses Feature, um Deinen Zeitaufwand zu minimieren!
                  </p>
                  <p style={{ margin: '0 0 12px' }}>
                    Tritt unserer Telegram-Community bei für Austausch mit Mitgliedern und Moderatoren: <a href="https://t.me/einfach_trading_de" target="_blank" rel="noopener noreferrer" style={{ color: COLORS.cyan, fontWeight: 700, textDecoration: 'none' }}>Telegram-Kanal öffnen</a>
                  </p>
                  <p style={{ margin: '12px 0 0' }}>
                    Im Hintergrund arbeiten wir ständig an weiteren Funktionen, um Deinem Trading noch mehr Power zu geben.
                  </p>
                </div>
                <div style={{
                  marginTop: '22px',
                  padding: '16px',
                  borderRadius: '14px',
                  border: `1px solid ${COLORS.border}`,
                  background: themeMode === 'light' ? 'rgba(255,255,255,0.58)' : 'rgba(7,16,21,0.28)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '14px',
                  flexWrap: 'wrap',
                  maxWidth: '860px',
                }}>
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: '800', marginBottom: '4px' }}>
                      Startseite nach dem Login
                    </div>
                    <div style={{ color: COLORS.muted, fontSize: '13px', lineHeight: 1.6 }}>
                      Diese Auswahl wird in Deinem Konto gespeichert und beim nächsten Login automatisch geöffnet.
                    </div>
                    {preferredStartViewMsg && <div style={{ color: COLORS.green, fontSize: '12px', marginTop: '4px' }}>✓ {preferredStartViewMsg}</div>}
                    {preferredStartViewErr && <div style={{ color: COLORS.red, fontSize: '12px', marginTop: '4px' }}>⚠ {preferredStartViewErr}</div>}
                  </div>
                  <select
                    value={preferredStartViewValue}
                    onChange={(event) => speicherePreferredStartView(event.target.value)}
                    style={{
                      ...inputStyle,
                      width: '220px',
                      minWidth: '190px',
                      margin: 0,
                      cursor: 'pointer',
                    }}
                  >
                    {navViews.map((navView) => (
                      <option key={navView} value={navView}>
                        {VIEW_LABELS[navView] || navView}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </Card>

            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.7fr) minmax(280px, 0.55fr)', gap: '24px' }}>
              <Card>
                <h2 style={{ margin: '0 0 14px', fontSize: '18px' }}>Risikohinweis / Haftungsausschluss</h2>
                <div style={{ color: COLORS.muted, fontSize: '14px', lineHeight: 1.75 }}>
                  {RISK_DISCLAIMER_SECTIONS.map(section => (
                    <section key={section.title} style={{ marginBottom: '14px' }}>
                      <h3 style={{ margin: '0 0 6px', color: COLORS.text, fontSize: '14px' }}>{section.title}</h3>
                      <p style={{ margin: 0 }}>{section.text}</p>
                    </section>
                  ))}
                </div>
                {riskAcceptanceText && (
                  <div style={{ marginTop: '14px', color: COLORS.muted, fontSize: '13px', lineHeight: 1.7 }}>
                    {riskAcceptanceText}
                  </div>
                )}
              </Card>

              <Card>
                <h2 style={{ margin: '0 0 14px', fontSize: '18px' }}>News</h2>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {changelogEntries.length === 0 && (
                    <div style={{ color: COLORS.muted, fontSize: '14px' }}>
                      Noch keine Einträge vorhanden.
                    </div>
                  )}
                  {changelogEntries.map((entry, index) => (
                    <div
                      key={`${entry.entry_date}-${index}`}
                      style={{
                        padding: '12px 14px',
                        borderRadius: '12px',
                        background: '#ffffff05',
                        border: `1px solid ${COLORS.border}`,
                      }}
                    >
                      <div style={{ fontSize: '11px', fontWeight: '700', letterSpacing: '0.08em', textTransform: 'uppercase', color: COLORS.cyan, marginBottom: '6px' }}>
                        {formatDate(entry.entry_date)}
                      </div>
                      <div style={{ color: COLORS.text, fontSize: '14px', lineHeight: 1.6 }}>{entry.change}</div>
                    </div>
                  ))}
                </div>
              </Card>
            </div>
          </div>
        )}

        {/* ── KONTO ── */}
        {view === 'konto' && (
          <ErrorBoundary COLORS={COLORS} sectionLabel="Konto">
            <AccountSection
              COLORS={COLORS}
              th={th}
              td={td}
              inputStyle={inputStyle}
              PLAN_FEATURES={PLAN_FEATURES}
              FEATURE_TOOLTIPS={FEATURE_TOOLTIPS}
              RSL_WEEKDAYS={RSL_WEEKDAYS}
              accountPlan={accountPlan}
              accountPlanStatus={accountPlanStatus}
              subscriptionUntil={subscriptionUntil}
              billingStatus={billingStatus}
              billingCurrentPeriodEnd={billingCurrentPeriodEnd}
              billingCancelAtPeriodEnd={billingCancelAtPeriodEnd}
              billingAccessUntil={billingAccessUntil}
              billingDeletionScheduledFor={billingDeletionScheduledFor}
              billingCustomerId={billingCustomerId}
              billingHistory={billingHistory}
              billingNotice={billingNotice}
              billingConfig={billingConfig}
              billingActionLoading={billingActionLoading}
              starteBillingCheckout={starteBillingCheckout}
              oeffneBillingPortal={oeffneBillingPortal}
              canUsePremiumSignalEmails={canUsePremiumSignalEmails}
              canUseRslNotifications={canUseRslNotifications}
              showTradeJournalConfig={moduleAccess.trade_journal}
              email={email}
              tradeJournalLoading={tradeJournalLoading}
              tradeJournalAccountLabel={tradeJournalAccountLabel}
              setTradeJournalAccountLabel={setTradeJournalAccountLabel}
              tradeJournalQueryId={tradeJournalQueryId}
              setTradeJournalQueryId={setTradeJournalQueryId}
              tradeJournalFlexToken={tradeJournalFlexToken}
              setTradeJournalFlexToken={setTradeJournalFlexToken}
              tradeJournalFlexTokenSet={tradeJournalFlexTokenSet}
              tradeJournalAgentSecretSet={tradeJournalAgentSecretSet}
              tradeJournalAgentSecretValue={tradeJournalAgentSecretValue}
              tradeJournalAgentGenerating={tradeJournalAgentGenerating}
              tradeJournalLastSyncAt={tradeJournalLastSyncAt}
              tradeJournalLastSyncStatus={tradeJournalLastSyncStatus}
              tradeJournalLastSyncError={tradeJournalLastSyncError}
              tradeJournalErr={tradeJournalErr}
              tradeJournalMsg={tradeJournalMsg}
              tradeJournalSaving={tradeJournalSaving}
              speichereTradeJournalKonto={speichereTradeJournalKonto}
              generiereTradeJournalAgentSecret={generiereTradeJournalAgentSecret}
              emailNotificationsEnabled={emailNotificationsEnabled}
              setEmailNotificationsEnabled={setEmailNotificationsEnabled}
              premiumSignalEmailsEnabled={premiumSignalEmailsEnabled}
              setPremiumSignalEmailsEnabled={setPremiumSignalEmailsEnabled}
              rslEmailDays={rslEmailDays}
              emailTheme={emailTheme}
              setEmailTheme={setEmailTheme}
              toggleRslEmailDay={toggleRslEmailDay}
              notificationErr={notificationErr}
              notificationMsg={notificationMsg}
              notificationSaving={notificationSaving}
              speichereBenachrichtigungen={speichereBenachrichtigungen}
              twoFactorEnabled={twoFactorEnabled}
              twoFactorPending={twoFactorPending}
              setTwoFactorEnabled={setTwoFactorEnabled}
              twoFactorCurrentPassword={twoFactorCurrentPassword}
              setTwoFactorCurrentPassword={setTwoFactorCurrentPassword}
              twoFactorVerificationCode={twoFactorVerificationCode}
              setTwoFactorVerificationCode={setTwoFactorVerificationCode}
              twoFactorErr={twoFactorErr}
              twoFactorMsg={twoFactorMsg}
              twoFactorSaving={twoFactorSaving}
              speichereZweiFaktor={speichereZweiFaktor}
              bestaetigeZweiFaktor={bestaetigeZweiFaktor}
              pwCurrent={pwCurrent}
              setPwCurrent={setPwCurrent}
              pwNew={pwNew}
              setPwNew={setPwNew}
              pwConfirm={pwConfirm}
              setPwConfirm={setPwConfirm}
              pwErr={pwErr}
              pwMsg={pwMsg}
              pwSaving={pwSaving}
              passwortAendern={passwortAendern}
              deletePw={deletePw}
              newsletterStatus={newsletterStatus}
              setNewsletterStatus={setNewsletterStatus}
              keepNewsletter={keepNewsletter}
              setKeepNewsletter={setKeepNewsletter}
              setDeletePw={setDeletePw}
              deleteErr={deleteErr}
              deleteMsg={deleteMsg}
              deleteSaving={deleteSaving}
              accountLoeschen={accountLoeschen}
            />
          </ErrorBoundary>
        )}

        {/* ── ADMIN ── */}
        {view === 'admin' && (
          <ErrorBoundary COLORS={COLORS} sectionLabel="Admin">
            <AdminPanel
              onSessionExpired={resetClientSession}
              onIbTaxYearsChanged={ladeIbTaxYears}
              themeMode={themeMode}
              onToast={showToast}
              onConfirm={requestConfirm}
              COLORS={COLORS}
              th={th}
              td={td}
              inputStyle={inputStyle}
              PLAN_MODULE_MATRIX_ROWS={PLAN_MODULE_MATRIX_ROWS}
              MODULE_LIST={MODULE_LIST}
              PLAN_LIST={PLAN_LIST}
              PLAN_STATUS_LIST={PLAN_STATUS_LIST}
              SUPPORT_CATEGORIES={SUPPORT_CATEGORIES}
              SUPPORT_STATUSES={SUPPORT_STATUSES}
              SIGNAL_KINDS={SIGNAL_KINDS}
            />
          </ErrorBoundary>
        )}

        {/* ── KONTOAUSZUG ── */}
        {view === 'kontoauszug' && moduleAccess.kontoauszug && (
          <ErrorBoundary COLORS={COLORS} sectionLabel="IB-Auswertung">
            <IbAnalysisSection
              COLORS={COLORS}
              th={th}
              td={td}
              inputStyle={inputStyle}
              formatMoney={formatMoney}
              kontoauszugErr={kontoauszugErr}
              kontoauszugMsg={kontoauszugMsg}
              kontoauszugLoading={kontoauszugLoading}
              kontoauszugFile={kontoauszugFile}
              setKontoauszugFile={setKontoauszugFile}
              kontoauszugYear={kontoauszugYear}
              setKontoauszugYear={setKontoauszugYear}
              ibTaxYears={ibTaxYears}
              analysiereKontoauszug={analysiereKontoauszug}
              kontoauszugProgress={kontoauszugProgress}
              kontoauszugProgressText={kontoauszugProgressText}
              kontoauszugMeta={kontoauszugMeta}
              kontoauszugResults={kontoauszugResults}
              kontoauszugReferences={kontoauszugReferences}
              kontoauszugShowReferences={kontoauszugShowReferences}
              setKontoauszugShowReferences={setKontoauszugShowReferences}
              kontoauszugIssues={kontoauszugIssues}
            />
          </ErrorBoundary>
        )}

        {view === 'coaching' && canAccessCoaching && (
          <ErrorBoundary COLORS={COLORS} sectionLabel="Anleitung">
            <CoachingSection COLORS={COLORS} onNavigate={navigateToView} />
          </ErrorBoundary>
        )}

        {view === 'academy' && moduleAccess.academy && (
          <ErrorBoundary COLORS={COLORS} sectionLabel="Automation">
            <AutomationSection
              COLORS={COLORS}
              isAdmin={isAdmin}
              tradeJournalOrderFormEnabled={tradeJournalOrderFormEnabled}
              onSessionExpired={resetClientSession}
              onToast={showToast}
              tradeJournalAgentSecretSet={tradeJournalAgentSecretSet}
              tradeJournalAgentName={tradeJournalAgentName}
              tradeJournalAgentVersion={tradeJournalAgentVersion}
              tradeJournalAgentStatusLabel={tradeJournalAgentStatusLabel}
              tradeJournalAgentStatusColor={tradeJournalAgentStatusColor}
              tradeJournalAgentLastSeenAt={tradeJournalAgentLastSeenAt}
              tradeJournalAgentLastError={tradeJournalAgentLastError}
              tradeJournalAgentLastErrorAt={tradeJournalAgentLastErrorAt}
              tradeJournalAgentTwsConnected={tradeJournalAgentTwsConnected}
              tradeJournalAgentLastTwsConnectedAt={tradeJournalAgentLastTwsConnectedAt}
              tradeJournalAgentLiveTwsConnected={tradeJournalAgentLiveTwsConnected}
              tradeJournalAgentLiveLastTwsConnectedAt={tradeJournalAgentLiveLastTwsConnectedAt}
              tradeJournalAgentPaperTwsConnected={tradeJournalAgentPaperTwsConnected}
              tradeJournalAgentPaperLastTwsConnectedAt={tradeJournalAgentPaperLastTwsConnectedAt}
              tradeJournalAgentLastQuoteDurationMs={tradeJournalAgentLastQuoteDurationMs}
              onLiveAgentErrorCleared={(data) => applyTradeJournalAccountPayload(data)}
            />
          </ErrorBoundary>
        )}

        {view === 'support' && moduleAccess.support && (
          <ErrorBoundary COLORS={COLORS} sectionLabel="Support">
            <SupportSection
              isAdmin={isAdmin}
              COLORS={COLORS}
              inputStyle={inputStyle}
              themeMode={themeMode}
              supportTickets={supportTickets}
              supportTicketTotal={supportTicketTotal}
              supportTicketOffset={supportTicketOffset}
              supportTicketLimit={supportTicketLimit}
              supportDetail={supportDetail}
              supportLoading={supportLoading}
              supportErr={supportErr}
              supportMsg={supportMsg}
              supportStatusFilter={supportStatusFilter}
              setSupportStatusFilter={setSupportStatusFilter}
              supportDeleteConfirmText={supportDeleteConfirmText}
              setSupportDeleteConfirmText={setSupportDeleteConfirmText}
              supportSubject={supportSubject}
              setSupportSubject={setSupportSubject}
              supportCategory={supportCategory}
              setSupportCategory={setSupportCategory}
              supportMessage={supportMessage}
              setSupportMessage={setSupportMessage}
              supportReply={supportReply}
              setSupportReply={setSupportReply}
              supportStatusValue={supportStatusValue}
              setSupportStatusValue={setSupportStatusValue}
              SUPPORT_CATEGORIES={SUPPORT_CATEGORIES}
              SUPPORT_STATUSES={SUPPORT_STATUSES}
              ladeSupportTickets={ladeSupportTickets}
              ladeSupportTicketDetail={ladeSupportTicketDetail}
              supportTicketErstellen={supportTicketErstellen}
              supportAntwortSenden={supportAntwortSenden}
              supportStatusSpeichern={supportStatusSpeichern}
              supportTicketLoeschen={supportTicketLoeschen}
              supportTicketSchliessen={supportTicketSchliessen}
            />
          </ErrorBoundary>
        )}

        {view === 'signals' && moduleAccess.signals && (
          <ErrorBoundary COLORS={COLORS} sectionLabel="Signale">
            <SignalsSection
              isAdmin={isAdmin}
              COLORS={COLORS}
              inputStyle={inputStyle}
              signalsLoading={signalsLoading}
              signalsEntries={signalsEntries}
              marketSentiment={marketSentiment}
              marketSignalCards={marketSignalCards}
              signalsErr={signalsErr}
              signalsMsg={signalsMsg}
              marketSignalsErr={marketSignalsErr}
              marketSignalsMsg={marketSignalsMsg}
              signalEditId={signalEditId}
              signalKind={signalKind}
              setSignalKind={setSignalKind}
              signalText={signalText}
              setSignalText={setSignalText}
              signalRelatedId={signalRelatedId}
              setSignalRelatedId={setSignalRelatedId}
              signalFilter={signalFilter}
              setSignalFilter={setSignalFilter}
              signalViewMode={signalViewMode}
              setSignalViewMode={setSignalViewMode}
              signalOpenLimit={signalOpenLimit}
              setSignalOpenLimit={setSignalOpenLimit}
              signalCloseLimit={signalCloseLimit}
              setSignalCloseLimit={setSignalCloseLimit}
              SIGNAL_KINDS={SIGNAL_KINDS}
              offeneSignals={offeneSignals}
              openSignals={openSignals}
              geschlosseneOpenSignals={geschlosseneOpenSignals}
              geschlosseneSignals={geschlosseneSignals}
              sichtbareOpenSignals={sichtbareOpenSignals}
              sichtbareGeschlosseneSignals={sichtbareGeschlosseneSignals}
              closedSignalIds={closedSignalIds}
              signalMap={signalMap}
              ladeSignale={ladeSignale}
              signalSpeichern={signalSpeichern}
              marketSignalAlarmAendern={marketSignalAlarmAendern}
              onBlockedMarketSignalAlarm={() => {
                setMarketSignalsMsg('');
                setMarketSignalsErr('Alarm per E-Mail ist nur mit Plus verfügbar.');
                showPlusUpgradeHint('Alarm per E-Mail');
              }}
              canUseMarketSignalEmailAlerts={canUseMarketSignalEmailAlerts}
              signalBearbeiten={signalBearbeiten}
              signalLoeschen={signalLoeschen}
              setSignalEditId={setSignalEditId}
              setSignalsErr={setSignalsErr}
              setSignalsMsg={setSignalsMsg}
            />
          </ErrorBoundary>
        )}

        {/* ── SCREENER ── */}
        {view === 'screener' && moduleAccess.screener && (
          <ErrorBoundary COLORS={COLORS} sectionLabel="RSL Screener">
            <ScreenerSection
              cachedAt={cachedAt}
              cachedAtByMarket={cachedAtByMarket}
              marketDataAsOf={marketDataAsOf}
              statusMsg={statusMsg}
              ergebnis={ergebnis}
              laden={laden}
              screenerTabs={screenerTabs}
              screenerTab={screenerTab}
              setScreenerTab={setScreenerTab}
              onScreenerTabSelect={handleScreenerTabSelect}
              activeScreenerTab={activeScreenerTab}
              depot={depot}
              kaufen={kaufen}
              entfernen={entfernen}
              speichernDepotPosition={speichernDepotPosition}
              addDepotTranche={fuegeDepotTrancheHinzu}
              deleteDepotTranche={loescheDepotTranche}
              tickerInZwischenablage={tickerInZwischenablage}
              uiSettings={uiSettings}
              themeMode={themeMode}
              COLORS={COLORS}
              th={th}
              td={td}
              inputStyle={inputStyle}
              ScreenerTableComponent={ScreenerTabelle}
              canLoadMoreTopSignals={canLoadMoreTopSignals}
              showLoadMoreTopSignals={true}
              maxTopSignalsVisible={(isAdmin || isPlusPlan) ? 1000 : (isEssentialPlan ? 20 : 10)}
              onBlockedFeature={showPlusUpgradeHint}
              customWatchlistsEnabled={!!moduleAccess.custom_rsl_watchlists}
              customWatchlists={customRslWatchlists}
              customWatchlistsLoading={customRslWatchlistsLoading}
              customWatchlistsError={customRslWatchlistsError}
              createCustomWatchlist={createCustomRslWatchlist}
              updateCustomWatchlist={updateCustomRslWatchlist}
              deleteCustomWatchlist={deleteCustomRslWatchlist}
              addCustomWatchlistItem={addCustomRslWatchlistItem}
              deleteCustomWatchlistItem={deleteCustomRslWatchlistItem}
              runCustomWatchlists={runCustomRslWatchlists}
              reportCustomWatchlistIssues={reportCustomRslWatchlistIssues}
              previewTradingViewWatchlist={previewTradingViewWatchlist}
              importTradingViewWatchlist={importTradingViewWatchlist}
              isAdmin={isAdmin}
            />
          </ErrorBoundary>
        )}

        {view === 'rsl_backtest' && moduleAccess.rsl_backtest && (
          <ErrorBoundary COLORS={COLORS} sectionLabel="RSL Backtest">
            <RslBacktestSection
              COLORS={COLORS}
              th={th}
              td={td}
              inputStyle={inputStyle}
              onSessionExpired={resetClientSession}
              canExportCsv={canExportBacktestCsv}
              canAccessExtendedMarkets={canAccessExtendedBacktestMarkets}
              onBlockedFeature={showPlusUpgradeHint}
              themeMode={themeMode}
              isAdmin={isAdmin}
            />
          </ErrorBoundary>
        )}

        {view === 'index_invest' && canAccessIndexInvest && (
          <ErrorBoundary COLORS={COLORS} sectionLabel="Index-Invest">
            <IndexInvestSection
              COLORS={COLORS}
              onSessionExpired={resetClientSession}
              themeMode={themeMode}
              canExportCsv={canExportIndexInvestCsv}
            />
          </ErrorBoundary>
        )}

        {view === 'trade_journal' && moduleAccess.trade_journal && (
          <ErrorBoundary COLORS={COLORS} sectionLabel="Trade Journal">
            <TradeJournalSection
              COLORS={COLORS}
              th={th}
              td={td}
              tradeJournalSourceFilter={tradeJournalSourceFilter}
              setTradeJournalSourceFilter={setTradeJournalSourceFilter}
              tradeJournalSyncLoading={tradeJournalSyncLoading}
              tradeJournalSyncStatus={tradeJournalSyncStatus}
              tradeJournalSyncProgress={tradeJournalSyncProgress}
              tradeJournalSyncMessage={tradeJournalSyncMessage}
              tradeJournalSyncFetchedCount={tradeJournalSyncFetchedCount}
              tradeJournalSyncImportedCount={tradeJournalSyncImportedCount}
              tradeJournalSyncSkippedCount={tradeJournalSyncSkippedCount}
              tradeJournalSyncImportedItems={tradeJournalSyncImportedItems}
              tradeJournalSyncError={tradeJournalSyncError}
              tradeJournalLastSyncAt={tradeJournalLastSyncAt}
              tradeJournalLastSyncImportedCount={tradeJournalLastSyncImportedCount}
              tradeJournalLastSyncSkippedCount={tradeJournalLastSyncSkippedCount}
              tradeJournalLastSyncItems={tradeJournalLastSyncItems}
              tradeJournalGroups={tradeJournalGroups}
              tradeJournalGroupPage={tradeJournalGroupPage}
              tradeJournalGroupPageLoading={tradeJournalGroupPageLoading}
              tradeJournalOverview={tradeJournalOverview}
              tradeJournalOverviewLoading={tradeJournalOverviewLoading}
              tradeJournalCashSummary={tradeJournalCashSummary}
              tradeJournalCashTransactions={tradeJournalCashTransactions}
              tradeJournalCashTransactionsLoaded={tradeJournalCashTransactionsLoaded}
              tradeJournalStatementSummary={tradeJournalStatementSummary}
              tradeJournalForexPnl={tradeJournalForexPnl}
              tradeJournalSetups={tradeJournalSetups}
              tradeJournalLivePnl={tradeJournalLivePnl}
              tradeJournalLivePnlLoading={tradeJournalLivePnlLoading}
              tradeJournalLivePnlError={tradeJournalLivePnlError}
              ladeTradeJournalGroups={ladeTradeJournalGroups}
              ladeTradeJournalGroupsPage={ladeTradeJournalGroupsPage}
              ladeTradeJournalOverview={ladeTradeJournalOverview}
              ladeTradeJournalCashSummary={ladeTradeJournalCashSummary}
              ladeTradeJournalDashboard={ladeTradeJournalDashboard}
              invalidateTradeJournalDashboard={invalidateTradeJournalDashboard}
              ladeTradeJournalCashTransactions={ladeTradeJournalCashTransactions}
              aktualisiereTradeJournalLivePnl={aktualisiereTradeJournalLivePnl}
              starteTradeJournalSync={starteTradeJournalSync}
              importiereTradeJournalDatei={importiereTradeJournalDatei}
              loescheTradeJournalEintraege={loescheTradeJournalEintraege}
              tradeJournalSetupAnlegen={tradeJournalSetupAnlegen}
              tradeJournalSetupBearbeiten={tradeJournalSetupBearbeiten}
              tradeJournalSetupLoeschen={tradeJournalSetupLoeschen}
              tradeJournalGroupSetupsSpeichern={tradeJournalGroupSetupsSpeichern}
              tradeJournalBulkGroupSetupsSpeichern={tradeJournalBulkGroupSetupsSpeichern}
              tradeJournalGruppeBearbeiten={tradeJournalGruppeBearbeiten}
              tradeJournalGruppeLoeschen={tradeJournalGruppeLoeschen}
              tradeJournalManuelleEintraegeAnlegen={tradeJournalManuelleEintraegeAnlegen}
            />
          </ErrorBoundary>
        )}

        {view === 'rsl_journal' && moduleAccess.depot && (
          <ErrorBoundary COLORS={COLORS} sectionLabel="RSL Journal">
            <RslJournalSection
              depotPerformanceRows={depotPerformanceRows}
              loescheHistorieEintrag={loescheHistorieEintrag}
              bearbeiteHistorieEintrag={bearbeiteHistorieEintrag}
              abbrecheHistorieBearbeitung={abbrecheHistorieBearbeitung}
              speichereHistorieEintrag={speichereHistorieEintrag}
              historyEditId={historyEditId}
              historyEditDraft={historyEditDraft}
              setHistoryEditDraft={setHistoryEditDraft}
              historyEditErr={historyEditErr}
              historyEditSaving={historyEditSaving}
              COLORS={COLORS}
              th={th}
              td={td}
              inputStyle={inputStyle}
              formatMoney={formatMoney}
              getPositionCurrencyLabel={getPositionCurrencyLabel}
              canExportCsv={canExportJournalCsv}
              onBlockedExport={() => showPlusUpgradeHint('CSV-Export im Journal')}
              themeMode={themeMode}
            />
          </ErrorBoundary>
        )}

        {/* ── DEPOT ── */}
        {view === 'depot' && moduleAccess.depot && (
          <ErrorBoundary COLORS={COLORS} sectionLabel="Depot">
            <DepotSection
              depot={depot}
              depotLaden={depotLaden}
              depotSort={depotSort}
              toggleDepotSort={toggleDepotSort}
              depotSP500={depotSP500}
              depotHDAX={depotHDAX}
              depotNASDAQ100={depotNASDAQ100}
              depotSTOXX600={depotSTOXX600}
              depotCustom={depotCustom}
              entfernen={entfernen}
              speichernDepotPosition={speichernDepotPosition}
              addDepotTranche={fuegeDepotTrancheHinzu}
              deleteDepotTranche={loescheDepotTranche}
              tickerInZwischenablage={tickerInZwischenablage}
              uiSettings={uiSettings}
              themeMode={themeMode}
              depotPerformanceRows={depotPerformanceRows}
              loescheHistorieEintrag={loescheHistorieEintrag}
              bearbeiteHistorieEintrag={bearbeiteHistorieEintrag}
              abbrecheHistorieBearbeitung={abbrecheHistorieBearbeitung}
              speichereHistorieEintrag={speichereHistorieEintrag}
              historyEditId={historyEditId}
              historyEditDraft={historyEditDraft}
              setHistoryEditDraft={setHistoryEditDraft}
              historyEditErr={historyEditErr}
              historyEditSaving={historyEditSaving}
              manuellerTicker={manuellerTicker}
              setManuellerTicker={setManuellerTicker}
              manuellHinzufuegen={manuellHinzufuegen}
              tickerFehler={tickerFehler}
              tickerErfolg={tickerErfolg}
              COLORS={COLORS}
              th={th}
              td={td}
              inputStyle={inputStyle}
              formatMoney={formatMoney}
              getPositionCurrencyLabel={getPositionCurrencyLabel}
              getDepotSectionIndexRsl={getDepotSectionIndexRsl}
              DepotRowsComponent={DepotRows}
              apiRequest={apiRequest}
            />
          </ErrorBoundary>
        )}

      </div>
      <AppFooter COLORS={COLORS} />
      {manualAddIndexChoice && (
        <div style={{
          position: 'fixed',
          inset: 0,
          zIndex: 960,
          background: 'rgba(7,16,21,0.82)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '22px',
        }}>
          <Card style={{ maxWidth: '560px', width: '100%', border: `1px solid ${COLORS.border}` }}>
            <div style={{ color: COLORS.muted, fontSize: '12px', fontWeight: '700', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '8px' }}>
              Index auswählen
            </div>
            <div style={{ fontSize: '20px', fontWeight: '800', marginBottom: '8px' }}>
              {manualAddIndexChoice.symbol} ist in mehreren Indizes enthalten
            </div>
            <div style={{ color: COLORS.muted, fontSize: '14px', lineHeight: 1.7, marginBottom: '14px' }}>
              Bitte wähle, in welchem Index der Titel hinzugefügt werden soll.
            </div>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              {manualAddIndexChoice.markets.map(market => (
                <Btn
                  key={market}
                  onClick={() => manuellHinzufuegenMitIndex(market)}
                  color="green"
                  disabled={manualAddIndexChoice.saving}
                >
                  {MANUAL_ADD_MARKET_LABELS[market] || market}
                </Btn>
              ))}
              <Btn
                onClick={() => setManualAddIndexChoice(null)}
                color="ghost"
                disabled={manualAddIndexChoice.saving}
              >
                Abbrechen
              </Btn>
            </div>
          </Card>
        </div>
      )}
      {closePosition && (
        <div style={{
          position: 'fixed',
          inset: 0,
          zIndex: 950,
          background: 'rgba(7,16,21,0.82)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '22px',
        }}>
          <Card style={{ maxWidth: '620px', width: '100%', border: `1px solid ${COLORS.border}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '14px', alignItems: 'flex-start', marginBottom: '16px' }}>
              <div>
                <div style={{ color: COLORS.muted, fontSize: '12px', fontWeight: '700', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '6px' }}>
                  Position schließen / verkaufen
                </div>
              </div>
              <Badge color="indigo">{closePosition.index}</Badge>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginBottom: '14px' }}>
              <div>
                <div style={{ color: COLORS.muted, fontSize: '12px', marginBottom: '6px' }}>Verkaufsdatum</div>
                <input
                  type="date"
                  value={closeDate}
                  onChange={event => setCloseDate(event.target.value)}
                  style={inputStyle}
                />
              </div>
              <div>
                <div style={{ color: COLORS.muted, fontSize: '12px', marginBottom: '6px' }}>Verkaufskurs</div>
                <input
                  type="number"
                  min="0"
                  step="0.000001"
                  value={closePrice}
                  onChange={event => setClosePrice(event.target.value)}
                  style={inputStyle}
                />
              </div>
              <div>
                <div style={{ color: COLORS.muted, fontSize: '12px', marginBottom: '6px' }}>Verkaufsmenge</div>
                <input
                  type="number"
                  min="0"
                  step="0.000001"
                  value={closeSellQuantity}
                  onChange={event => setCloseSellQuantity(event.target.value)}
                  placeholder="leer = komplett schließen"
                  style={inputStyle}
                />
              </div>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: '10px',
              padding: '12px',
              borderRadius: '14px',
              border: `1px solid ${COLORS.border}`,
              background: '#ffffff05',
              marginBottom: '14px',
            }}>
              <div>
                <div style={{ color: COLORS.muted, fontSize: '11px', marginBottom: '4px' }}>Menge</div>
                <div style={{ fontWeight: '700' }}>{Number.isFinite(closeSellQuantityValue) ? formatMoney(closeSellQuantityValue) : '0,00'}</div>
              </div>
              <div>
                <div style={{ color: COLORS.muted, fontSize: '11px', marginBottom: '4px' }}>Einstand</div>
                <div style={{ fontWeight: '700' }}>{Number.isFinite(closeEntryPrice) ? `${formatMoney(closeEntryPrice)}${closeCurrency ? ` ${closeCurrency}` : ''}` : `0,00${closeCurrency ? ` ${closeCurrency}` : ''}`}</div>
              </div>
              <div>
                <div style={{ color: COLORS.muted, fontSize: '11px', marginBottom: '4px' }}>Verkaufswert</div>
                <div style={{ fontWeight: '700' }}>{closeSaleValue !== null ? `${formatMoney(closeSaleValue)}${closeCurrency ? ` ${closeCurrency}` : ''}` : '—'}</div>
              </div>
              <div>
                <div style={{ color: COLORS.muted, fontSize: '11px', marginBottom: '4px' }}>Realisiert</div>
                <div style={{
                  fontWeight: '800',
                  color: closeRealizedPl === null || closeRealizedPl === 0 ? COLORS.muted : (closeRealizedPl > 0 ? COLORS.green : COLORS.red),
                }}>
                  {closeRealizedPl !== null ? `${formatMoney(closeRealizedPl)}${closeCurrency ? ` ${closeCurrency}` : ''}` : '—'}
                </div>
              </div>
            </div>

            {closeDataIncomplete && (
              <div style={{
                padding: '10px 12px',
                borderRadius: '12px',
                border: '1px solid rgba(245, 158, 11, 0.35)',
                background: 'rgba(245, 158, 11, 0.12)',
                color: COLORS.orange,
                fontSize: '13px',
                marginBottom: '12px',
              }}>
                Bitte erst alle Tranchen mit Menge und Einstandskurs pflegen. Nicht gepflegte Positionen werden mit 0 berücksichtigt.
              </div>
            )}
            {closeErr && <div style={{ color: COLORS.red, fontSize: '13px', marginBottom: '12px' }}>⚠ {closeErr}</div>}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', flexWrap: 'wrap' }}>
              <Btn
                onClick={() => {
                  if (closeSaving) return;
                  setClosePosition(null);
                  setCloseErr('');
                  setCloseSellQuantity('');
                }}
                color="ghost"
                disabled={closeSaving}
              >
                Abbrechen
              </Btn>
              <Btn onClick={schliesseDepotPosition} color="red" disabled={closeSaving}>
                {closeSaving ? 'Schließe...' : 'Trade schließen'}
              </Btn>
            </div>
          </Card>
        </div>
      )}
      <ConfirmDialog
        open={confirmState.open}
        message={confirmState.message}
        onCancel={() => closeConfirm(false)}
        onConfirm={() => closeConfirm(true)}
        COLORS={COLORS}
      />
      <ToastHost toast={toast} onDismiss={dismissToast} COLORS={COLORS} />
    </div>
  );
}
